// /partners/:id/edit — editor de partner con tabs: General / Branding / Plans / Clients.
//
// Patron comun: cada tab fetchea su propio data via TanStack Query y
// escribe via useMutation con invalidateQueries al success.

import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { AlertTriangle, ArrowLeft, Check, Plus, Star, Trash2, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  fetchPartnerDetail,
  patchPartner,
  listPartnerPlans,
  createPlanApi,
  patchPlan,
  deletePlan,
  listPartnerClients,
  createClientApi,
  patchClient,
  deleteClientApi,
  listClientPresets,
  upsertClientPreset,
  deleteClientPreset,
  adminRotateWebhookSecret,
  fetchPartnerCredentials,
  runPartnerCredentialAction,
  listPartnerTosVersions,
  publishTosVersion,
  listFavoriteTypes,
  listFavoriteItems,
  fetchPartnerPaymentConfig,
  putPartnerPaymentConfig,
  fetchPartnerInvoicingConfig,
  putPartnerInvoicingConfig,
  type InvoicingFieldDef,
  type InvoicingProbe,
  type PaymentProviderConfigEntry,
  type PaymentProviderFieldDef,
  type BrandingConfig,
  type PartnerDetail,
  type AdminPlan,
  type AdminClient,
  type LoginPresetUpsertBody,
  type PresetKind,
  type PlanLimits,
  type RotatedWebhookSecret,
  type PartnerCredentialAction,
  type PartnerJwksSlot,
  type PartnerJwksStatus,
  type SignupRequiredFavorite,
  type FavoriteType,
  type FormSchemaField,
  AdminApiError,
} from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import BrandingEditor from '@/components/BrandingEditor';
import ConfirmDialog from '@/components/ConfirmDialog';
import ClientOverridesModal from '@/components/ClientOverridesModal';
import PlanFeaturesEditor, { fromRows, type FeatureRow } from '@/components/PlanFeaturesEditor';
import type { HelpId } from '@/help/targets';
import { fieldCls } from '@/lib/form-styles';
import { canWrite as canWriteSection } from '@/lib/access';

type Tab = 'general' | 'branding' | 'signup' | 'plans' | 'clients' | 'terms' | 'danger';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'general', label: 'General' },
  { key: 'branding', label: 'Branding' },
  { key: 'signup', label: 'Signup' },
  { key: 'plans', label: 'Plans' },
  { key: 'clients', label: 'Clients' },
  { key: 'terms', label: 'Términos' },
];

// Solo visible con el permiso danger_zone en la cuenta (ademas del rol admin).
// El backend re-verifica: ocultar la pestaña es UX, no seguridad.
const DANGER_TAB: { key: Tab; label: string } = { key: 'danger', label: 'Danger Zone' };

/**
 * Capitulo del manual de cada solapa. La ficha del partner es una sola ruta
 * con siete pantallas adentro: sin esto, "el manual de esta pantalla" abriria
 * siempre el mismo capitulo sin importar en cual de las siete estes.
 */
const TAB_HELP: Record<Tab, HelpId> = {
  general: 'partner.general',
  branding: 'partner.branding',
  signup: 'partner.signup',
  plans: 'partner.plans',
  clients: 'partner.clients',
  terms: 'partner.terms',
  danger: 'partner.danger',
};

function errorMessage(e: unknown): string {
  return e instanceof AdminApiError ? e.message : 'Error desconocido.';
}

export default function PartnerEdit(): JSX.Element {
  const { partnerId = '' } = useParams<{ partnerId: string }>();
  const [tab, setTab] = useState<Tab>('general');
  const session = useSession();
  const canWrite = canWriteSection(session, 'partners');
  const hasDangerZone = canWrite && session?.permissions?.['danger_zone'] === true;
  const visibleTabs = hasDangerZone ? [...TABS, DANGER_TAB] : TABS;

  const detailQ = useQuery({
    queryKey: ['admin-partner-detail', partnerId],
    queryFn: () => fetchPartnerDetail(partnerId),
    enabled: !!partnerId,
  });

  if (detailQ.isLoading) return <p className="text-sm text-ink-muted">Cargando partner…</p>;
  if (detailQ.isError || !detailQ.data) {
    return (
      <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
        No pudimos cargar este partner. <Link to="/partners" className="underline">Volver</Link>
      </div>
    );
  }

  const partner = detailQ.data;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 data-help-page={TAB_HELP[tab]} data-help="partner.page" className="text-2xl font-semibold">
            {partner.display_name}
          </h1>
          <p className="text-xs font-mono text-ink-muted">{partner.partner_id}</p>
        </div>
        <Link to="/partners" className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink dark:hover:text-ink-inverted hover:underline">
          <ArrowLeft size={14} aria-hidden="true" />
          Volver
        </Link>
      </header>

      {!canWrite ? (
        <div role="status" className="rounded-lg border border-status-warning/30 bg-status-warning/10 p-3 text-sm text-status-warning">
          Tu perfil tiene solo lectura sobre Partners. Los botones de guardar están deshabilitados.
        </div>
      ) : null}

      <nav
        data-help="partner.tabs"
        aria-label="Secciones del partner"
        className="flex gap-1 border-b border-surface-border dark:border-surface-dark-border"
      >
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            type="button"
            data-help={TAB_HELP[t.key]}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${
              tab === t.key
                ? t.key === 'danger'
                  ? 'border-status-error text-status-error font-medium'
                  : 'border-brand text-brand font-medium'
                : t.key === 'danger'
                  ? 'border-transparent text-status-error/70 hover:text-status-error'
                  : 'border-transparent text-ink-muted hover:text-ink dark:hover:text-ink-inverted'
            }`}
          >
            {t.key === 'danger' ? (
              <AlertTriangle size={12} className="inline -mt-0.5 mr-1" aria-hidden="true" />
            ) : null}
            {t.label}
          </button>
        ))}
      </nav>

      <section>
        {tab === 'general' ? <GeneralTab partner={partner} canWrite={canWrite} /> : null}
        {tab === 'branding' ? <BrandingTab partner={partner} canWrite={canWrite} /> : null}
        {tab === 'signup' ? <SignupTab partner={partner} canWrite={canWrite} /> : null}
        {tab === 'plans' ? <PlansTab partner={partner} canWrite={canWrite} /> : null}
        {tab === 'clients' ? (
          <ClientsTab
            partner={partner}
            canWrite={canWrite}
            canEditDanger={hasDangerZone}
          />
        ) : null}
        {tab === 'terms' ? <TermsTab partnerId={partnerId} canWrite={canWrite} /> : null}
        {tab === 'danger' && hasDangerZone ? <DangerZoneTab partner={partner} /> : null}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// General tab
// ---------------------------------------------------------------------------

function GeneralTab({ partner, canWrite }: { partner: PartnerDetail; canWrite: boolean }): JSX.Element {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    display_name: partner.display_name,
    status: partner.status,
    webhook_url: partner.webhook_url ?? '',
    access_token_ttl_seconds: partner.access_token_ttl_seconds,
    refresh_token_ttl_seconds: partner.refresh_token_ttl_seconds,
    entitlements_cache_ttl_seconds: partner.entitlements_cache_ttl_seconds,
    payment_provider_default: partner.payment_provider_default,
  });
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      patchPartner(partner.partner_id, {
        display_name: form.display_name,
        status: form.status,
        webhook_url: form.webhook_url || null,
        access_token_ttl_seconds: form.access_token_ttl_seconds,
        refresh_token_ttl_seconds: form.refresh_token_ttl_seconds,
        entitlements_cache_ttl_seconds: form.entitlements_cache_ttl_seconds,
        payment_provider_default: form.payment_provider_default,
      }),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['admin-partner-detail', partner.partner_id] });
      setTimeout(() => setSaved(false), 3000);
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    save.mutate();
  }

  return (
    <form onSubmit={onSubmit} aria-busy={save.isPending ? 'true' : undefined} className="space-y-4 max-w-3xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Display name">
          <input
            type="text"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            disabled={!canWrite}
            className={fieldCls({ size: 'lg' })}
          />
        </Field>
        <Field label="Status">
          <select
            value={form.status}
            onChange={(e) =>
              setForm({ ...form, status: e.target.value as PartnerDetail['status'] })
            }
            disabled={!canWrite}
            className={fieldCls({ size: 'lg' })}
          >
            <option value="active">active</option>
            <option value="suspended">suspended</option>
            <option value="offboarded">offboarded</option>
          </select>
        </Field>
        <Field label="Webhook URL (opcional)">
          <input
            type="url"
            value={form.webhook_url}
            onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
            disabled={!canWrite}
            placeholder="https://partner.com/webhooks/authsys"
            className={fieldCls({ size: 'lg' })}
          />
        </Field>
        <Field label="Payment provider default">
          <select
            value={form.payment_provider_default}
            onChange={(e) =>
              setForm({
                ...form,
                payment_provider_default: e.target.value as PartnerDetail['payment_provider_default'],
              })
            }
            disabled={!canWrite}
            className={fieldCls({ size: 'lg' })}
          >
            <option value="line">line</option>
            <option value="mercadopago">mercadopago</option>
            <option value="stripe">stripe</option>
          </select>
        </Field>
        <Field label="Access token TTL (seconds, 60-86400)" helpId="partner.general.ttl">
          <input
            type="number"
            min={60}
            max={86400}
            value={form.access_token_ttl_seconds}
            onChange={(e) =>
              setForm({ ...form, access_token_ttl_seconds: parseInt(e.target.value, 10) || 0 })
            }
            disabled={!canWrite}
            className={fieldCls({ size: 'lg' })}
          />
        </Field>
        <Field label="Refresh token TTL (seconds, 3600-31536000)" helpId="partner.general.ttl">
          <input
            type="number"
            min={3600}
            max={31536000}
            value={form.refresh_token_ttl_seconds}
            onChange={(e) =>
              setForm({ ...form, refresh_token_ttl_seconds: parseInt(e.target.value, 10) || 0 })
            }
            disabled={!canWrite}
            className={fieldCls({ size: 'lg' })}
          />
        </Field>
        <Field label="Entitlements cache TTL (seconds, 0-86400)" helpId="partner.general.ttl">
          <input
            type="number"
            min={0}
            max={86400}
            value={form.entitlements_cache_ttl_seconds}
            onChange={(e) =>
              setForm({ ...form, entitlements_cache_ttl_seconds: parseInt(e.target.value, 10) || 0 })
            }
            disabled={!canWrite}
            className={fieldCls({ size: 'lg' })}
          />
        </Field>
      </div>

      {save.isError ? (
        <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
          {errorMessage(save.error)}
        </div>
      ) : null}
      {saved ? (
        <div role="status" className="rounded-lg border border-status-success/30 bg-status-success/10 p-3 text-sm text-status-success">
          Guardado.
        </div>
      ) : null}

      <button
        type="submit"
        disabled={!canWrite || save.isPending}
        className="min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white font-medium px-6 py-2 focus-visible:ring-2 focus-visible:ring-brand-light disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {save.isPending ? 'Guardando…' : 'Guardar cambios'}
      </button>

      <GeneralDangerZone partner={partner} canWrite={canWrite} />
    </form>
  );
}

// ---------------------------------------------------------------------------
// Zona peligrosa del tab General
// ---------------------------------------------------------------------------

/**
 * Un solo marco para TODO lo que puede romper al partner en produccion: las
 * claves de firma OIDC, el api_secret HMAC y el secret de webhooks. Las tres
 * comparten el mismo riesgo — una rotacion mal hecha deja a los usuarios sin
 * poder loguear o le corta la integracion al partner — asi que van juntas y
 * encuadradas, no sueltas entre los campos de configuracion.
 *
 * Ojo: esto NO es el tab "Danger Zone" (ese es `DangerZoneTab`, detras del
 * permiso danger_zone y con los switches). Este bloque vive en General porque
 * son credenciales del partner, y basta el rol admin.
 */
function GeneralDangerZone({
  partner,
  canWrite,
}: {
  partner: PartnerDetail;
  canWrite: boolean;
}): JSX.Element {
  return (
    <section
      aria-labelledby="general-danger-zone"
      className="mt-8 rounded-xl border border-status-error/40 bg-status-error/5 p-4 sm:p-5 space-y-5"
    >
      <header className="space-y-1">
        <h3
          id="general-danger-zone"
          className="flex items-center gap-2 text-base font-semibold text-status-error"
        >
          <AlertTriangle size={16} aria-hidden="true" />
          Zona peligrosa
        </h3>
        <p className="text-xs text-ink-muted max-w-2xl">
          Credenciales del partner. Generarlas o rotarlas afecta a usuarios reales: puede
          dejarlos sin poder loguear o cortarle la integración al partner. Cada acción avisa
          qué rompe antes de ejecutarse.
        </p>
      </header>

      <div data-help="partner.credentials">
        <CredentialsSection partner={partner} canWrite={canWrite} />
      </div>

      <div className="pt-5 border-t border-status-error/20" data-help="partner.webhook-secret">
        <RotateWebhookSecretSection partner={partner} canWrite={canWrite} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Credenciales: claves de firma OIDC (JWKS) + api_secret HMAC
// ---------------------------------------------------------------------------

/**
 * Sin `jwks.configured` el partner devuelve 500 en el primer login OIDC, y sin
 * `api_secret_configured` el authorizer rechaza todo request S2S. Las dos cosas
 * se provisionan solas al crear el partner; esta seccion existe para verlas,
 * rotarlas, y reparar los partners viejos que quedaron a medias.
 *
 * Rotacion en dos pasos a proposito (ver libs/crypto/jwks-provision.ts): se
 * prepara la clave, se publica un rato para que los verificadores del partner
 * la levanten, y recien despues se pasa a firmar con ella.
 */
function CredentialsSection({
  partner,
  canWrite,
}: {
  partner: PartnerDetail;
  canWrite: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const [pending, setPending] = useState<PartnerCredentialAction | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);

  const credsQ = useQuery({
    queryKey: ['admin-partner-credentials', partner.partner_id],
    queryFn: () => fetchPartnerCredentials(partner.partner_id),
  });

  const mut = useMutation({
    mutationFn: (action: PartnerCredentialAction) =>
      runPartnerCredentialAction(partner.partner_id, action),
    onSuccess: (data) => {
      setPending(null);
      setRevealedSecret(data.api_secret ?? null);
      qc.setQueryData(['admin-partner-credentials', partner.partner_id], {
        partner_id: data.partner_id,
        jwks: data.jwks,
        api_secret_configured: data.api_secret_configured,
      });
    },
  });

  const creds = credsQ.data;
  const jwks = creds?.jwks;

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold">Claves de firma OIDC</h4>
        <p className="text-xs text-ink-muted max-w-2xl mt-1">
          Con estas el partner firma los id_token que emite. Se generan solas al crear el
          partner.
        </p>
      </div>

      {credsQ.isLoading ? <p className="text-sm text-ink-muted">Cargando credenciales…</p> : null}
      {credsQ.isError ? (
        <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
          No pudimos leer el estado de las credenciales. {errorMessage(credsQ.error)}
        </div>
      ) : null}

      {creds && jwks ? (
        <>
          {!jwks.configured ? (
            <div role="alert" className="rounded-lg border border-status-error/40 bg-status-error/10 p-3 text-sm space-y-2">
              <p className="font-medium text-status-error">
                Este partner no tiene clave de firma: el login OIDC le va a devolver error 500.
              </p>
              <p className="text-xs text-ink-muted">
                Pasa con partners creados antes de que el alta provisionara las claves sola.
                Generarlas es seguro y no afecta a nadie más.
              </p>
            </div>
          ) : null}

          {jwks.read_error ? (
            <div role="alert" className="rounded-lg border border-status-warning/40 bg-status-warning/10 p-3 text-xs text-status-warning">
              Los kids están en la base pero no pudimos leerlos de Secrets Manager:{' '}
              {jwks.read_error}
            </div>
          ) : null}

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <SlotCard
              label="Clave de firma (activa)"
              hint="Con esta se firman los id_token."
              slot={jwks.current}
              emptyLabel="Sin clave"
            />
            <SlotCard
              label="Clave secundaria"
              hint="Publicada en el JWKS, no se firma con ella."
              slot={jwks.secondary}
              emptyLabel="Ninguna (sin rotación en curso)"
            />
          </dl>

          <div className="flex flex-wrap gap-2">
            {!jwks.configured ? (
              <ActionButton
                label="Generar claves de firma"
                primary
                disabled={!canWrite || mut.isPending}
                onClick={() => setPending('jwks_provision')}
              />
            ) : null}
            {jwks.configured && !jwks.secondary ? (
              <ActionButton
                label="Preparar rotación"
                disabled={!canWrite || mut.isPending}
                onClick={() => setPending('jwks_stage')}
              />
            ) : null}
            {jwks.configured && jwks.secondary ? (
              <>
                <ActionButton
                  label="Activar clave preparada"
                  primary
                  disabled={!canWrite || mut.isPending}
                  onClick={() => setPending('jwks_promote')}
                />
                <ActionButton
                  label="Descartar secundaria"
                  danger
                  disabled={!canWrite || mut.isPending}
                  onClick={() => setPending('jwks_discard_secondary')}
                />
              </>
            ) : null}
          </div>

          <div className="pt-5 border-t border-status-error/20 space-y-2">
            <h4 className="text-sm font-semibold">api_secret (HMAC server-to-server)</h4>
            <p className="text-xs text-ink-muted max-w-2xl">
              Con este el backend del partner firma sus requests a nuestra API.{' '}
              {creds.api_secret_configured ? (
                <span className="text-status-success">Configurado.</span>
              ) : (
                <span className="text-status-error">
                  Sin configurar — el backend del partner recibe 401 en todos los endpoints.
                </span>
              )}
            </p>
            <ActionButton
              label={creds.api_secret_configured ? 'Rotar api_secret' : 'Generar api_secret'}
              danger={creds.api_secret_configured}
              disabled={!canWrite || mut.isPending}
              onClick={() => setPending('rotate_api_secret')}
            />
          </div>
        </>
      ) : null}

      {mut.isError ? (
        <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
          {errorMessage(mut.error)}
        </div>
      ) : null}

      {revealedSecret ? (
        <div
          role="status"
          aria-live="polite"
          className="rounded-lg border border-status-warning/40 bg-status-warning/10 p-4 text-sm space-y-2"
        >
          <p className="font-medium text-status-warning">
            Guardá este api_secret y compartilo con el partner. NO se vuelve a mostrar.
          </p>
          <div className="font-mono text-xs break-all bg-surface-muted dark:bg-surface-dark rounded p-2 border border-surface-border dark:border-surface-dark-border">
            {revealedSecret}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setRevealedSecret(null);
            }}
            className="text-xs text-ink-muted underline"
          >
            Cerrar (ya copié el secret)
          </button>
        </div>
      ) : null}

      <ConfirmDialog
        open={pending !== null}
        title={pending ? CREDENTIAL_ACTIONS[pending].title : ''}
        description={
          pending ? (
            <div className="space-y-2">
              {CREDENTIAL_ACTIONS[pending].describe(partner, jwks ?? null)}
              {mut.isError ? <p className="text-xs text-status-error">{errorMessage(mut.error)}</p> : null}
            </div>
          ) : null
        }
        confirmLabel={pending ? CREDENTIAL_ACTIONS[pending].confirmLabel : 'Confirmar'}
        danger={pending ? CREDENTIAL_ACTIONS[pending].danger : false}
        loading={mut.isPending}
        onConfirm={() => {
          if (pending) mut.mutate(pending);
        }}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}

/**
 * Texto de cada confirmacion. Cada accion dice explicitamente que se rompe y
 * que no: son operaciones que pueden dejar al partner sin poder loguear, y el
 * admin las dispara desde una UI sin poder leer el codigo.
 */
const CREDENTIAL_ACTIONS: Record<
  PartnerCredentialAction,
  {
    title: string;
    confirmLabel: string;
    danger: boolean;
    describe: (partner: PartnerDetail, jwks: PartnerJwksStatus | null) => ReactNode;
  }
> = {
  jwks_provision: {
    title: 'Generar claves de firma',
    confirmLabel: 'Generar',
    danger: false,
    describe: (partner) => (
      <p>
        Vas a generar la clave RS256 con la que <strong>{partner.display_name}</strong> va a
        firmar sus id_token. No afecta nada existente — hoy el partner no puede emitir tokens.
      </p>
    ),
  },
  jwks_stage: {
    title: 'Preparar rotación',
    confirmLabel: 'Preparar',
    danger: false,
    describe: () => (
      <div className="space-y-2">
        <p>
          Se genera una clave nueva y se publica en el JWKS, pero se sigue firmando con la
          actual. Nada se rompe: es el paso previo para que los verificadores del partner
          levanten la clave nueva antes de que empiece a usarse.
        </p>
        <p className="text-xs text-ink-muted">
          Los clientes suelen cachear el JWKS 1 hora. Esperá al menos eso antes de activarla.
        </p>
      </div>
    ),
  },
  jwks_promote: {
    title: 'Activar clave preparada',
    confirmLabel: 'Activar',
    danger: true,
    describe: (_partner, jwks) => (
      <div className="space-y-2">
        <p>
          A partir de ahora se firma con <code className="font-mono text-xs">{jwks?.secondary?.kid}</code>.
          La clave anterior queda publicada en el JWKS, así que los id_token ya emitidos siguen
          validando hasta que expiren.
        </p>
        {jwks?.current?.alg && jwks?.secondary?.alg && jwks.current.alg !== jwks.secondary.alg ? (
          <p className="text-xs text-status-warning">
            Ojo: cambia el algoritmo de {jwks.current.alg} a {jwks.secondary.alg}. Los SDK que
            tengan el algoritmo fijado por config van a rechazar los tokens nuevos.
          </p>
        ) : null}
      </div>
    ),
  },
  jwks_discard_secondary: {
    title: 'Descartar clave secundaria',
    confirmLabel: 'Descartar',
    danger: true,
    describe: (_partner, jwks) => (
      <p>
        <code className="font-mono text-xs">{jwks?.secondary?.kid}</code> sale del JWKS público.
        Todo id_token firmado con ella deja de validar en el acto. Hacelo solo si ya expiraron
        (TTL del id_token, típicamente 1 hora).
      </p>
    ),
  },
  rotate_api_secret: {
    title: 'Rotar api_secret',
    confirmLabel: 'Rotar',
    danger: true,
    describe: (partner) => (
      <div className="space-y-2">
        <p>
          Vas a generar un nuevo secreto HMAC para <strong>{partner.display_name}</strong>. El
          backend del partner tiene que actualizarlo o sus requests van a empezar a fallar con 401.
        </p>
        <p className="text-xs text-ink-muted">
          El authorizer cachea 15 min por instancia: la firma vieja puede seguir siendo aceptada
          durante esa ventana. El plaintext se muestra una sola vez.
        </p>
      </div>
    ),
  },
};

function SlotCard({
  label,
  hint,
  slot,
  emptyLabel,
}: {
  label: string;
  hint: string;
  slot: PartnerJwksSlot | null;
  emptyLabel: string;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-surface-border dark:border-surface-dark-border p-3 space-y-1">
      <dt className="text-xs font-medium text-ink-muted">{label}</dt>
      <dd className="font-mono text-xs break-all">
        {slot ? (
          <>
            {slot.kid}
            {slot.alg ? <span className="ml-2 font-sans text-ink-muted">({slot.alg})</span> : null}
          </>
        ) : (
          <span className="font-sans text-ink-muted">{emptyLabel}</span>
        )}
      </dd>
      <p className="text-xs text-ink-muted">{hint}</p>
    </div>
  );
}

function ActionButton({
  label,
  onClick,
  disabled,
  primary = false,
  danger = false,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  primary?: boolean;
  danger?: boolean;
}): JSX.Element {
  const tone = danger
    ? 'border border-status-error/50 text-status-error hover:bg-status-error/10'
    : primary
      ? 'bg-brand hover:bg-brand-hover text-white font-medium'
      : 'border border-surface-border dark:border-surface-dark-border hover:bg-surface-muted dark:hover:bg-surface-dark-muted';

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      disabled={disabled}
      className={`min-h-[2.75rem] rounded-lg px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed ${tone}`}
    >
      {label}
    </button>
  );
}

function RotateWebhookSecretSection({
  partner,
  canWrite,
}: {
  partner: PartnerDetail;
  canWrite: boolean;
}): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [revealed, setRevealed] = useState<RotatedWebhookSecret | null>(null);

  const mut = useMutation({
    mutationFn: () => adminRotateWebhookSecret(partner.partner_id),
    onSuccess: (data) => {
      setRevealed(data);
      setConfirming(false);
    },
  });

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold">Webhook secret</h4>
      <p className="text-xs text-ink-muted max-w-2xl">
        Con este firmamos los webhooks que le mandamos al partner. La rotación lo reemplaza:
        los próximos webhooks van a usar el nuevo y el partner tiene que actualizar su
        verificador.
        {partner.webhook_secret_configured
          ? ' Hay un secret configurado actualmente.'
          : ' No hay secret configurado actualmente.'}
      </p>

      {/*
        Huella del secret vigente. Sin esto no había forma de saber cuál está
        activo, y ante la duda el reflejo era rotar de nuevo — que invalida el
        que se acaba de entregar. Pasó dos veces (2026-07-11 y 2026-08-10).
      */}
      {partner.webhook_secret_last_rotation ? (
        <div className="rounded-lg border border-surface-border dark:border-surface-dark-border p-3 text-xs space-y-1">
          <div className="flex flex-wrap gap-x-2 gap-y-1 items-baseline">
            <span className="text-ink-muted">Secret vigente:</span>
            <span className="font-mono">
              {partner.webhook_secret_last_rotation.fingerprint ?? '(sin huella registrada)'}
            </span>
          </div>
          <p className="text-ink-muted">
            Rotado el{' '}
            {new Date(partner.webhook_secret_last_rotation.rotated_at).toLocaleString('es-AR')}.
            Para confirmar que el partner tiene el correcto, pedile que corra{' '}
            <code className="font-mono">
              printf &apos;%s&apos; &quot;$SECRETO&quot; | sha256sum | cut -c1-16
            </code>{' '}
            y compare contra esa huella. No hace falta rotar para verificar.
          </p>
        </div>
      ) : null}

      <ActionButton
        label="Rotar webhook secret"
        danger
        // Mientras hay un secret recién generado en pantalla y sin confirmar,
        // rotar otra vez lo invalidaría en silencio. Hay que cerrar el panel
        // ("ya copié el secret") antes de poder volver a rotar.
        disabled={!canWrite || revealed !== null}
        onClick={() => setConfirming(true)}
      />
      {revealed !== null ? (
        <p className="text-xs text-ink-muted">
          Cerrá el recuadro de abajo para poder rotar de nuevo.
        </p>
      ) : null}

      {revealed ? (
        <div
          role="status"
          aria-live="polite"
          className="rounded-lg border border-status-warning/40 bg-status-warning/10 p-4 text-sm space-y-2"
        >
          <p className="font-medium text-status-warning">{revealed.message}</p>
          <div className="font-mono text-xs break-all bg-surface-muted dark:bg-surface-dark rounded p-2 border border-surface-border dark:border-surface-dark-border">
            {revealed.webhook_secret}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setRevealed(null);
            }}
            className="text-xs text-ink-muted underline"
          >
            Cerrar (ya copié el secret)
          </button>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirming}
        title="Rotar webhook secret"
        description={
          <div className="space-y-2">
            <p>
              Vas a generar un nuevo secret HMAC para <strong>{partner.display_name}</strong>.
              El viejo queda invalidado inmediatamente — los webhooks que lleguen al partner
              con la firma vieja van a fallar la verificación.
            </p>
            <p className="text-xs text-ink-muted">
              El plaintext se muestra una sola vez. Asegurate de coordinar con el partner
              antes de rotar.
            </p>
            {partner.webhook_secret_last_rotation ? (
              <p className="text-xs text-status-warning">
                Ya hay un secret vigente (huella{' '}
                <span className="font-mono">
                  {partner.webhook_secret_last_rotation.fingerprint ?? '—'}
                </span>
                , rotado el{' '}
                {new Date(partner.webhook_secret_last_rotation.rotated_at).toLocaleString('es-AR')}
                ). Si ya se lo entregaste al partner, rotar de nuevo lo deja inservible y
                hay que volver a coordinar la entrega. Si solo querés verificar cuál tiene,
                comparás huellas — no hace falta rotar.
              </p>
            ) : null}
            {mut.isError ? (
              <p className="text-xs text-status-error">
                {mut.error instanceof AdminApiError ? mut.error.message : 'Error'}
              </p>
            ) : null}
          </div>
        }
        confirmLabel="Rotar"
        danger
        loading={mut.isPending}
        onConfirm={() => mut.mutate()}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}

function Field({
  label,
  children,
  helpId,
}: {
  label: string;
  children: ReactNode;
  /** Punto del manual que explica este campo (modo señalar y recorridos). */
  helpId?: HelpId;
}): JSX.Element {
  return (
    <label className="text-sm space-y-1 block" data-help={helpId}>
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Branding tab
// ---------------------------------------------------------------------------

const EMPTY_BRANDING: BrandingConfig = {
  display_name: 'Partner',
  // Vacio: el dominio assets.authsys.com no existe. El admin pega la URL real
  // del logo del partner; si la deja vacia el portal degrada a header solo-texto.
  logo_url: '',
  theme_mode: 'auto',
  colors: {
    primary: '#2563eb',
    accent: '#0ea5e9',
    background: '#ffffff',
    surface: '#f8fafc',
    text: '#0f172a',
    text_muted: '#475569',
    error: '#dc2626',
    success: '#15803d',
  },
  colors_dark: {
    primary: '#3b82f6',
    accent: '#38bdf8',
    background: '#0b1220',
    surface: '#111827',
    text: '#f1f5f9',
    text_muted: '#94a3b8',
    error: '#ef4444',
    success: '#4ade80',
  },
  font_family: "'Inter', system-ui, sans-serif",
};

function BrandingTab({ partner, canWrite }: { partner: PartnerDetail; canWrite: boolean }): JSX.Element {
  const qc = useQueryClient();
  const [branding, setBranding] = useState<BrandingConfig>(
    partner.branding ?? { ...EMPTY_BRANDING, display_name: partner.display_name },
  );
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () => patchPartner(partner.partner_id, { branding }),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['admin-partner-detail', partner.partner_id] });
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const clear = useMutation({
    mutationFn: () => patchPartner(partner.partner_id, { branding: null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-partner-detail', partner.partner_id] }),
  });

  return (
    <div className="space-y-4 max-w-4xl">
      <BrandingEditor value={branding} onChange={setBranding} />

      {save.isError ? (
        <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
          {errorMessage(save.error)}
        </div>
      ) : null}
      {saved ? (
        <div role="status" className="rounded-lg border border-status-success/30 bg-status-success/10 p-3 text-sm text-status-success">
          Branding guardado. Cache Redis invalidada.
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={!canWrite || save.isPending}
          className="min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white font-medium px-6 py-2 disabled:opacity-50"
        >
          {save.isPending ? 'Guardando…' : 'Guardar branding'}
        </button>
        <button
          type="button"
          onClick={() => clear.mutate()}
          disabled={!canWrite || clear.isPending || !partner.branding}
          className="min-h-[2.75rem] rounded-lg border border-surface-border dark:border-surface-dark-border px-4 py-2 text-sm hover:bg-surface-muted dark:hover:bg-surface-dark disabled:opacity-50"
        >
          Volver a defaults
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Signup tab — tipos de favorito requeridos al alta de cuenta
// ---------------------------------------------------------------------------

function SignupTab({ partner, canWrite }: { partner: PartnerDetail; canWrite: boolean }): JSX.Element {
  const qc = useQueryClient();
  const [entries, setEntries] = useState<SignupRequiredFavorite[]>(
    partner.signup_required_favorites,
  );
  const [saved, setSaved] = useState(false);

  // Catalogo de favorite_types del partner para llenar el selector "tipo".
  // Includes archived → filtramos a 'active' en el componente (archivados no
  // pueden agregarse, pero los que ya estan en la config se siguen mostrando
  // para que el admin pueda quitarlos).
  const typesQ = useQuery({
    queryKey: ['admin-favorite-types', partner.partner_id],
    queryFn: () => listFavoriteTypes(partner.partner_id),
  });

  const save = useMutation({
    mutationFn: () =>
      patchPartner(partner.partner_id, { signup_required_favorites: entries }),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['admin-partner-detail', partner.partner_id] });
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const types = typesQ.data ?? [];
  const activeTypes = types.filter((t) => t.status === 'active');
  const usedTypeIds = new Set(entries.map((e) => e.favorite_type_id));
  const availableToAdd = activeTypes.filter((t) => !usedTypeIds.has(t.favorite_type_id));

  function addEntry(typeId: string): void {
    const t = types.find((x) => x.favorite_type_id === typeId);
    if (!t) return;
    setEntries([
      ...entries,
      {
        favorite_type_id: typeId,
        title_field: 'name',
        initial_limit: 20,
        ...(t.name && { label: t.name }),
      },
    ]);
  }

  function updateEntry(
    idx: number,
    transform: (entry: SignupRequiredFavorite) => SignupRequiredFavorite,
  ): void {
    setEntries(entries.map((e, i) => (i === idx ? transform(e) : e)));
  }

  function removeEntry(idx: number): void {
    setEntries(entries.filter((_, i) => i !== idx));
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="rounded-lg border border-surface-border dark:border-surface-dark-border bg-surface-muted dark:bg-surface-dark-muted p-3 text-sm space-y-1">
        <p className="font-medium">Favoritos obligatorios al crear cuenta</p>
        <p className="text-xs text-ink-muted">
          Para cada tipo agregado, el form de signup va a mostrar un buscador con
          los items activos del catalogo. El usuario debe elegir uno o la opcion
          <strong> &ldquo;Ninguno&rdquo;</strong> (no se crea favorito si elige Ninguno).
          Solo aplica a usuarios nuevos — los existentes no se ven afectados.
        </p>
      </div>

      {typesQ.isLoading ? (
        <p className="text-sm text-ink-muted">Cargando tipos de favorito…</p>
      ) : null}

      {entries.length === 0 ? (
        <p className="text-sm text-ink-muted">
          Ningun tipo es obligatorio al signup. Agrega uno desde el selector de abajo.
        </p>
      ) : (
        <ul className="space-y-3">
          {entries.map((entry, idx) => {
            const t = types.find((x) => x.favorite_type_id === entry.favorite_type_id);
            return (
              <li
                key={entry.favorite_type_id}
                className="bg-surface dark:bg-surface-dark rounded-lg border border-surface-border dark:border-surface-dark-border p-3 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">
                      {t?.name ?? entry.favorite_type_id}{' '}
                      <span className="text-xs font-mono text-ink-muted">
                        ({entry.favorite_type_id})
                      </span>
                    </p>
                    {t?.status === 'archived' ? (
                      <p className="text-xs text-status-warning">
                        Tipo archivado — el signup-config publico va a ignorarlo
                        hasta que lo reactives.
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeEntry(idx)}
                    disabled={!canWrite}
                    className="text-sm text-status-error hover:underline disabled:opacity-50"
                  >
                    Quitar
                  </button>
                </div>
                <SignupFavoriteEntryFields
                  entry={entry}
                  type={t ?? null}
                  partnerId={partner.partner_id}
                  disabled={!canWrite}
                  onUpdate={(transform) => updateEntry(idx, transform)}
                />
              </li>
            );
          })}
        </ul>
      )}

      {availableToAdd.length > 0 ? (
        <div className="flex items-center gap-2">
          <label className="text-sm text-ink-muted">
            Agregar tipo:{' '}
            <select
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) {
                  addEntry(e.target.value);
                  e.target.value = '';
                }
              }}
              disabled={!canWrite}
              className={fieldCls({ block: false, extra: 'ml-1' })}
            >
              <option value="">— elegi un tipo —</option>
              {availableToAdd.map((t) => (
                <option key={t.favorite_type_id} value={t.favorite_type_id}>
                  {t.name} ({t.favorite_type_id})
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : activeTypes.length === 0 ? (
        <p className="text-xs text-ink-muted">
          No hay favorite_types activos. Crea uno desde la seccion Catalogo
          antes de configurarlo como requerido.
        </p>
      ) : null}

      {save.isError ? (
        <div
          role="alert"
          className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error"
        >
          {errorMessage(save.error)}
        </div>
      ) : null}
      {saved ? (
        <div
          role="status"
          className="rounded-lg border border-status-success/30 bg-status-success/10 p-3 text-sm text-status-success"
        >
          Guardado. Cache del partner invalidada.
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => save.mutate()}
        disabled={!canWrite || save.isPending}
        className="min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white font-medium px-6 py-2 disabled:opacity-50"
      >
        {save.isPending ? 'Guardando…' : 'Guardar configuracion'}
      </button>
    </div>
  );
}

/**
 * Helpers para listar las keys disponibles en `favorite_items.details` de un
 * tipo dado. Hay dos fuentes:
 *   1. `favorite_types.details.form_schema.fields[]` — la "API contract" del
 *      tipo, definida por el partner al crear el tipo.
 *   2. Inspeccion de items existentes — muestra keys que aparecen en items
 *      reales aunque no esten en el schema (legacy / overrides).
 * Tomamos (1) como autoridad y (2) como augmenting cuando hace falta.
 */
function getFieldKeysFromSchema(type: FavoriteType | null): FormSchemaField[] {
  if (!type) return [];
  const schema = type.details?.['form_schema'] as { fields?: FormSchemaField[] } | undefined;
  return schema?.fields ?? [];
}

function SignupFavoriteEntryFields({
  entry,
  type,
  partnerId,
  disabled,
  onUpdate,
}: {
  entry: SignupRequiredFavorite;
  type: FavoriteType | null;
  partnerId: string;
  disabled: boolean;
  onUpdate: (transform: (entry: SignupRequiredFavorite) => SignupRequiredFavorite) => void;
}): JSX.Element {
  // Sample 5 items existentes del tipo para que el admin tenga una preview de
  // como se vera el select. Si no hay items, mostramos un warning.
  const sampleQ = useQuery({
    queryKey: ['admin-favorite-items-sample', partnerId, entry.favorite_type_id],
    queryFn: () =>
      listFavoriteItems(partnerId, {
        favorite_type_id: entry.favorite_type_id,
        status: 'active',
        limit: 5,
      }),
    enabled: !!entry.favorite_type_id,
  });

  const schemaFields = getFieldKeysFromSchema(type);
  // title_field puede ser "name" (built-in) o cualquier campo string/text/url
  const titleOptions = [
    { value: 'name', label: 'name (columna built-in)' },
    ...schemaFields
      .filter((f) => f.type === 'string' || f.type === 'text' || f.type === 'url')
      .map((f) => ({ value: `details.${f.key}`, label: `details.${f.key} (${f.label})` })),
  ];
  // image_field solo URL fields (o vacio para "sin imagen")
  const imageOptions = [
    { value: '', label: '— sin imagen —' },
    ...schemaFields
      .filter((f) => f.type === 'url')
      .map((f) => ({ value: `details.${f.key}`, label: `details.${f.key} (${f.label})` })),
  ];

  const sampleItems = sampleQ.data?.items ?? [];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="text-sm space-y-1 block">
          <span className="font-medium">Label en el form</span>
          <input
            type="text"
            value={entry.label ?? ''}
            onChange={(e) => {
              const next = e.target.value;
              onUpdate((prev) => {
                if (next === '') {
                  const { label: _drop, ...rest } = prev;
                  void _drop;
                  return rest as SignupRequiredFavorite;
                }
                return { ...prev, label: next };
              });
            }}
            placeholder={type?.name ?? 'Equipo favorito'}
            disabled={disabled}
            maxLength={100}
            className={fieldCls()}
          />
        </label>
        <label className="text-sm space-y-1 block">
          <span className="font-medium">Campo titulo</span>
          <select
            value={entry.title_field}
            onChange={(e) => {
              const next = e.target.value;
              onUpdate((prev) => ({ ...prev, title_field: next }));
            }}
            disabled={disabled}
            className={fieldCls()}
          >
            {titleOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm space-y-1 block">
          <span className="font-medium">Campo imagen (URL)</span>
          <select
            value={entry.image_field ?? ''}
            onChange={(e) => {
              const next = e.target.value;
              onUpdate((prev) => {
                if (next === '') {
                  // Borrar la key (vs ponerla a undefined) para que el JSON
                  // publicado no incluya `image_field: undefined`.
                  const { image_field: _drop, ...rest } = prev;
                  void _drop;
                  return rest as SignupRequiredFavorite;
                }
                return { ...prev, image_field: next };
              });
            }}
            disabled={disabled}
            className={fieldCls()}
          >
            {imageOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="text-sm space-y-1 block max-w-xs">
        <span className="font-medium">Items mostrados antes de buscar</span>
        <input
          type="number"
          min={1}
          max={100}
          value={entry.initial_limit}
          onChange={(e) => {
            const next = Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 20));
            onUpdate((prev) => ({ ...prev, initial_limit: next }));
          }}
          disabled={disabled}
          className={fieldCls()}
        />
      </label>

      {schemaFields.length === 0 && type ? (
        <p className="text-xs text-status-warning">
          Este tipo no tiene un form_schema declarado en su details. Solo se puede
          usar <code>name</code> como titulo. Configura el schema desde Catalogo
          &gt; tipo &quot;{type.code}&quot; para habilitar mas opciones.
        </p>
      ) : null}

      <div className="rounded border border-surface-border dark:border-surface-dark-border p-2 bg-surface-muted/40 dark:bg-surface-dark-muted/40">
        <p className="text-xs font-medium text-ink-muted mb-2">Preview del select:</p>
        {sampleQ.isLoading ? (
          <p className="text-xs text-ink-muted">Cargando items de muestra…</p>
        ) : sampleItems.length === 0 ? (
          <p className="text-xs text-status-warning">
            No hay items activos de este tipo todavia. El form de signup va a
            mostrar solo &ldquo;Ninguno&rdquo;.
          </p>
        ) : (
          <ul className="space-y-1">
            {sampleItems.map((item) => {
              const titleVal = projectField(item, entry.title_field);
              const imageVal = entry.image_field
                ? projectField(item, entry.image_field)
                : null;
              return (
                <li key={item.favorite_id} className="flex items-center gap-2 text-sm">
                  {imageVal ? (
                    <img
                      src={imageVal}
                      alt=""
                      className="w-6 h-6 rounded object-cover"
                      onError={(e) => {
                        e.currentTarget.style.visibility = 'hidden';
                      }}
                    />
                  ) : (
                    <span className="w-6 h-6 rounded bg-ink-muted/20" aria-hidden />
                  )}
                  <span>{titleVal ?? `(${item.code})`}</span>
                </li>
              );
            })}
            <li className="text-sm text-ink-muted border-t border-surface-border dark:border-surface-dark-border pt-1 mt-1">
              <span className="w-6 h-6 rounded bg-ink-muted/20 inline-block align-middle mr-2" />
              Ninguno
            </li>
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Proyecta `title_field` / `image_field` (formato "name" o "details.<key>")
 * sobre un FavoriteItem. Devuelve string si el valor es string/number/bool,
 * sino null.
 */
function projectField(
  item: { name: string; details: Record<string, unknown> },
  ref: string,
): string | null {
  let value: unknown;
  if (ref === 'name') {
    value = item.name;
  } else if (ref.startsWith('details.')) {
    value = item.details?.[ref.slice('details.'.length)];
  } else {
    return null;
  }
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}

// ---------------------------------------------------------------------------
// Plans tab
// ---------------------------------------------------------------------------

function PlansTab({
  partner,
  canWrite,
}: {
  partner: PartnerDetail;
  canWrite: boolean;
}): JSX.Element {
  const partnerId = partner.partner_id;
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const plansQ = useQuery({
    queryKey: ['admin-plans', partnerId],
    queryFn: () => listPartnerPlans(partnerId),
  });

  // El destacado vive en el partner (un solo plan por partner), no en cada
  // plan: marcar uno nuevo desmarca el anterior sin pasos extra.
  const setRecommended = useMutation({
    mutationFn: (planId: string | null) => patchPartner(partnerId, { recommended_plan_id: planId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-partner-detail', partnerId] }),
  });

  const recommendedId = partner.recommended_plan_id;

  // Archivar el plan destacado tiene que soltar el destaque en el mismo gesto.
  // Si no, el partner queda apuntando a un plan que ya no se vende: la
  // pantalla del usuario lo ignora, pero el backoffice mostraría "destacado"
  // sobre algo que no hace nada.
  const clearRecommendedIfArchived = (planId: string): void => {
    if (planId === recommendedId) setRecommended.mutate(null);
  };

  const toggleStatus = useMutation({
    mutationFn: (plan: AdminPlan) =>
      patchPlan(plan.plan_id, {
        status: plan.status === 'active' ? 'archived' : 'active',
      }),
    onSuccess: (_res, plan) => {
      void qc.invalidateQueries({ queryKey: ['admin-plans', partnerId] });
      if (plan.status === 'active') clearRecommendedIfArchived(plan.plan_id);
    },
  });

  const removePlan = useMutation({
    mutationFn: (planId: string) => deletePlan(planId),
    onSuccess: (_res, planId) => {
      void qc.invalidateQueries({ queryKey: ['admin-plans', partnerId] });
      clearRecommendedIfArchived(planId);
    },
  });
  const recommendedPlan = plansQ.data?.find((p) => p.plan_id === recommendedId);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink-muted">
          {plansQ.data?.length ?? 0} plans configurados.
        </p>
        <button
          type="button"
          onClick={() => setCreating(true)}
          disabled={!canWrite}
          className="min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 disabled:opacity-50"
        >
          Nuevo plan
        </button>
      </div>

      {plansQ.data?.length === 0 ? (
        <p className="text-sm text-ink-muted">Sin plans configurados.</p>
      ) : null}

      {/* Plan destacado. Es lo único de esta pantalla que el usuario final ve
          en su pantalla de cambio de plan, así que se explica en primera
          persona en vez de dejar una estrella sin contexto. */}
      <div className="rounded-lg border border-surface-border dark:border-surface-dark-border p-3 text-sm space-y-1">
        <p className="font-medium flex items-center gap-1.5">
          <Star size={14} className="text-brand" aria-hidden="true" />
          Plan destacado
        </p>
        <p className="text-ink-muted">
          {recommendedPlan ? (
            <>
              <strong>{recommendedPlan.display_name}</strong> se muestra con la chapita
              &ldquo;Recomendado&rdquo; y viene preseleccionado cuando el usuario entra a cambiar
              de plan.
            </>
          ) : recommendedId ? (
            // El plan quedó archivado: la FK lo sigue aceptando pero el
            // endpoint público no lo marca. Sin este aviso, el backoffice
            // diría "configurado" y la pantalla del usuario no mostraría nada.
            <>
              Apunta a <span className="font-mono">{recommendedId}</span>, que ya no está en la
              lista: la pantalla del usuario lo ignora y preselecciona el plan de mayor precio.
            </>
          ) : (
            <>
              Sin destacado: la pantalla de cambio de plan preselecciona el plan disponible de
              mayor precio.
            </>
          )}
        </p>
        {setRecommended.isError ? (
          <p role="alert" className="text-status-error">
            {errorMessage(setRecommended.error)}
          </p>
        ) : null}
      </div>

      {plansQ.data ? (
        <ul className="space-y-2">
          {plansQ.data.map((p) => (
            <li
              key={p.plan_id}
              className="bg-surface dark:bg-surface-dark rounded-lg border border-surface-border dark:border-surface-dark-border p-3 space-y-2"
            >
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[12rem]">
                  <p className="font-medium">
                    {p.display_name}{' '}
                    <span className="text-xs font-mono text-ink-muted">({p.code})</span>
                  </p>
                  <p className="text-xs text-ink-muted">
                    {p.price_amount} {p.price_currency} · {p.billing_period} ·{' '}
                    {p.max_concurrent_streams} streams · {p.max_devices} devices ·{' '}
                    {describeLimits(p.limits)}
                  </p>
                </div>
                <span
                  className={`text-xs rounded-full px-2 py-0.5 font-medium ${
                    p.status === 'active'
                      ? 'bg-status-success/20 text-status-success'
                      : 'bg-ink-muted/20 text-ink-muted'
                  }`}
                >
                  {p.status}
                </span>
                {/* Solo planes activos: destacar uno archivado no tendría
                    efecto (el endpoint público solo marca activos). */}
                {p.status === 'active' ? (
                  <button
                    type="button"
                    onClick={() =>
                      setRecommended.mutate(p.plan_id === recommendedId ? null : p.plan_id)
                    }
                    disabled={!canWrite || setRecommended.isPending}
                    aria-pressed={p.plan_id === recommendedId}
                    title={
                      p.plan_id === recommendedId
                        ? 'Quitar el destacado'
                        : 'Destacar este plan en la pantalla de cambio de plan'
                    }
                    className={`inline-flex items-center gap-1 text-sm hover:underline disabled:opacity-50 ${
                      p.plan_id === recommendedId
                        ? 'text-brand font-medium'
                        : 'text-ink-muted hover:text-ink dark:hover:text-ink-inverted'
                    }`}
                  >
                    <Star
                      size={14}
                      aria-hidden="true"
                      fill={p.plan_id === recommendedId ? 'currentColor' : 'none'}
                    />
                    {p.plan_id === recommendedId ? 'Destacado' : 'Destacar'}
                  </button>
                ) : null}
                {/* Precio, limites y config son columnas VERSIONADAS: cambiarlas
                    publica una version nueva. Ese flujo (con diff y motivo
                    obligatorio) vive en /plans/:planId. Editarlas aca ya no es
                    posible — el trigger plans_mirror_guard de la DB lo rechaza. */}
                <Link
                  to={`/plans/${encodeURIComponent(p.plan_id)}`}
                  className="text-sm text-brand hover:underline"
                >
                  Precio, limites e historial
                </Link>
                <button
                  type="button"
                  onClick={() => toggleStatus.mutate(p)}
                  disabled={!canWrite || toggleStatus.isPending}
                  className="text-sm text-ink-muted hover:text-ink dark:hover:text-ink-inverted hover:underline disabled:opacity-50"
                >
                  {p.status === 'active' ? 'Archivar' : 'Desarchivar'}
                </button>
                {p.status === 'active' ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        window.confirm(
                          `¿Eliminar plan "${p.display_name}"?\n\nQueda como archived. Subscripciones activas no se ven afectadas; nuevos usuarios no podrán suscribirse a este plan.`,
                        )
                      ) {
                        removePlan.mutate(p.plan_id);
                      }
                    }}
                    disabled={!canWrite || removePlan.isPending}
                    className="text-sm text-status-error hover:underline disabled:opacity-50"
                  >
                    Eliminar
                  </button>
                ) : null}
              </div>

            </li>
          ))}
        </ul>
      ) : null}

      {creating ? (
        <CreatePlanForm
          partnerId={partnerId}
          onClose={() => setCreating(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['admin-plans', partnerId] })}
        />
      ) : null}
    </div>
  );
}

function describeLimits(limits: PlanLimits | undefined): string {
  if (!limits) return 'favoritos: ilimitados';
  const total = limits.max_favorites;
  const perType = limits.max_favorites_per_type;
  const parts: string[] = [];
  if (total === null || total === undefined) parts.push('total: ilimitado');
  else if (total === 0) parts.push('total: bloqueado');
  else parts.push(`total: ${total}`);
  if (perType !== null && perType !== undefined) {
    parts.push(perType === 0 ? 'por tipo: bloqueado' : `por tipo: ${perType}`);
  }
  return `favoritos ${parts.join(', ')}`;
}

interface LimitFieldState {
  mode: 'unlimited' | 'blocked' | 'limited';
  value: number;
}

function stateToLimit(s: LimitFieldState): number | null {
  if (s.mode === 'unlimited') return null;
  if (s.mode === 'blocked') return 0;
  return s.value;
}

function LimitField({
  label,
  state,
  onChange,
  disabled,
}: {
  label: string;
  state: LimitFieldState;
  onChange: (s: LimitFieldState) => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <div className="text-sm space-y-1">
      <span className="font-medium block">{label}</span>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={state.mode}
          onChange={(e) =>
            onChange({ ...state, mode: e.target.value as LimitFieldState['mode'] })
          }
          disabled={disabled}
          className={fieldCls({ block: false })}
        >
          <option value="unlimited">Ilimitado</option>
          <option value="limited">Limite</option>
          <option value="blocked">Bloqueado</option>
        </select>
        {state.mode === 'limited' ? (
          <input
            type="number"
            min={1}
            max={10000}
            value={state.value}
            onChange={(e) =>
              onChange({ ...state, value: Math.max(1, parseInt(e.target.value, 10) || 1) })
            }
            disabled={disabled}
            className={fieldCls({ block: false, extra: 'w-28' })}
          />
        ) : null}
      </div>
    </div>
  );
}

function CreatePlanForm({
  partnerId,
  onClose,
  onCreated,
}: {
  partnerId: string;
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [form, setForm] = useState({
    plan_id: 'plan_',
    code: '',
    display_name: '',
    price_amount: 0,
    price_currency: 'ARS',
    billing_period: 'monthly' as 'monthly' | 'yearly' | 'weekly',
    max_concurrent_streams: 1,
    max_devices: 3,
    trial_days: 0,
  });
  const [favTotal, setFavTotal] = useState<LimitFieldState>({ mode: 'unlimited', value: 10 });
  const [favPerType, setFavPerType] = useState<LimitFieldState>({ mode: 'unlimited', value: 5 });
  const [featureRows, setFeatureRows] = useState<FeatureRow[]>([]);

  const mut = useMutation({
    mutationFn: () =>
      createPlanApi(partnerId, {
        ...form,
        features: fromRows(featureRows),
        limits: {
          max_favorites: stateToLimit(favTotal),
          max_favorites_per_type: stateToLimit(favPerType),
        },
      }),
    onSuccess: () => {
      onCreated();
      onClose();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mut.mutate();
      }}
      className="bg-surface-muted dark:bg-surface-dark-muted rounded-xl border border-surface-border dark:border-surface-dark-border p-4 space-y-3"
    >
      <h3 className="text-sm font-medium" data-help="partner.plans.new">
        Nuevo plan
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="plan_id (debe empezar con plan_)">
          <input
            type="text"
            required
            value={form.plan_id}
            onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
            className={fieldCls({ mono: true })}
          />
        </Field>
        <Field label="code">
          <input
            type="text"
            required
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            className={fieldCls()}
          />
        </Field>
        <Field label="Display name">
          <input
            type="text"
            required
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            className={fieldCls()}
          />
        </Field>
        <Field label="Price amount">
          <input
            type="number"
            min={0}
            required
            value={form.price_amount}
            onChange={(e) => setForm({ ...form, price_amount: parseFloat(e.target.value) || 0 })}
            className={fieldCls()}
          />
        </Field>
        <Field label="Price currency (3 chars)">
          <input
            type="text"
            maxLength={3}
            required
            value={form.price_currency}
            onChange={(e) => setForm({ ...form, price_currency: e.target.value.toUpperCase() })}
            className={fieldCls()}
          />
        </Field>
        <Field label="Billing period">
          <select
            value={form.billing_period}
            onChange={(e) =>
              setForm({
                ...form,
                billing_period: e.target.value as 'monthly' | 'yearly' | 'weekly',
              })
            }
            className={fieldCls()}
          >
            <option value="monthly">monthly</option>
            <option value="yearly">yearly</option>
            <option value="weekly">weekly</option>
          </select>
        </Field>
        <Field label="Max concurrent streams">
          <input
            type="number"
            min={1}
            max={100}
            value={form.max_concurrent_streams}
            onChange={(e) =>
              setForm({ ...form, max_concurrent_streams: parseInt(e.target.value, 10) || 1 })
            }
            className={fieldCls()}
          />
        </Field>
        <Field label="Max devices">
          <input
            type="number"
            min={1}
            max={100}
            value={form.max_devices}
            onChange={(e) => setForm({ ...form, max_devices: parseInt(e.target.value, 10) || 1 })}
            className={fieldCls()}
          />
        </Field>
      </div>
      <div className="border-t border-surface-border dark:border-surface-dark-border pt-3">
        <PlanFeaturesEditor rows={featureRows} onChange={setFeatureRows} />
      </div>
      <div className="border-t border-surface-border dark:border-surface-dark-border pt-3 space-y-3">
        <p className="text-xs text-ink-muted">
          Limites de favoritos (se pueden editar despues publicando una version
          del plan, desde "Precio, limites e historial").
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <LimitField
            label="Max total de favoritos"
            state={favTotal}
            onChange={setFavTotal}
            disabled={false}
          />
          <LimitField
            label="Max por tipo"
            state={favPerType}
            onChange={setFavPerType}
            disabled={false}
          />
        </div>
      </div>
      {mut.isError ? (
        <p className="text-sm text-status-error">{errorMessage(mut.error)}</p>
      ) : null}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="min-h-[2.5rem] rounded border border-surface-border dark:border-surface-dark-border px-3 text-sm"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={mut.isPending}
          className="min-h-[2.5rem] rounded bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 disabled:opacity-50"
        >
          {mut.isPending ? 'Creando…' : 'Crear plan'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Clients tab
// ---------------------------------------------------------------------------

function ClientsTab({
  partner,
  canWrite,
  canEditDanger,
}: {
  partner: PartnerDetail;
  canWrite: boolean;
  canEditDanger: boolean;
}): JSX.Element {
  const partnerId = partner.partner_id;
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const clientsQ = useQuery({
    queryKey: ['admin-clients', partnerId],
    queryFn: () => listPartnerClients(partnerId),
  });

  const del = useMutation({
    mutationFn: (clientId: string) => deleteClientApi(clientId),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ['admin-clients', partnerId] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink-muted">
          {clientsQ.data?.length ?? 0} OAuth clients configurados.
        </p>
        <button
          type="button"
          onClick={() => setCreating(true)}
          disabled={!canWrite}
          className="min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 disabled:opacity-50"
        >
          Nuevo client
        </button>
      </div>

      {clientsQ.data?.length === 0 ? (
        <p className="text-sm text-ink-muted">Sin clients configurados.</p>
      ) : null}

      {clientsQ.data ? (
        <ul className="space-y-2">
          {clientsQ.data.map((c) => (
            <ClientRow
              key={c.client_id}
              client={c}
              partner={partner}
              canWrite={canWrite}
              canEditDanger={canEditDanger}
              onDelete={() => setConfirmDelete(c.client_id)}
              onUpdated={() => qc.invalidateQueries({ queryKey: ['admin-clients', partnerId] })}
            />
          ))}
        </ul>
      ) : null}

      {creating ? (
        <CreateClientForm
          partnerId={partnerId}
          onClose={() => setCreating(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['admin-clients', partnerId] })}
        />
      ) : null}

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Eliminar OAuth client"
        description={
          <div className="space-y-2">
            <p>
              Vas a eliminar <strong>{confirmDelete}</strong>. Los tokens emitidos para este
              client quedan válidos hasta que expiren (no hacemos revoke en cadena).
            </p>
            {del.isError ? (
              <p className="text-status-error text-xs">{errorMessage(del.error)}</p>
            ) : null}
          </div>
        }
        confirmLabel="Eliminar"
        danger
        loading={del.isPending}
        onConfirm={() => confirmDelete && del.mutate(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

function ClientRow({
  client,
  partner,
  canWrite,
  canEditDanger,
  onDelete,
  onUpdated,
}: {
  client: AdminClient;
  partner: PartnerDetail;
  canWrite: boolean;
  canEditDanger: boolean;
  onDelete: () => void;
  onUpdated: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [overridesOpen, setOverridesOpen] = useState(false);
  const [redirects, setRedirects] = useState(client.redirect_uris.join('\n'));
  const overrideCount = Object.keys(client.config_overrides ?? {}).length;

  const save = useMutation({
    mutationFn: () =>
      patchClient(client.client_id, {
        redirect_uris: redirects
          .split('\n')
          .map((l) => l.trim())
          .filter((l) => l.length > 0),
      }),
    onSuccess: () => {
      setEditing(false);
      onUpdated();
    },
  });

  const togglePkce = useMutation({
    mutationFn: () => patchClient(client.client_id, { pkce_required: !client.pkce_required }),
    onSuccess: () => onUpdated(),
  });

  return (
    <li className="bg-surface dark:bg-surface-dark rounded-lg border border-surface-border dark:border-surface-dark-border p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm">{client.client_id}</span>
        <span className="text-xs rounded-full px-2 py-0.5 bg-ink-muted/20 text-ink-muted">
          {client.client_type}
        </span>
        {client.has_secret ? (
          <span className="text-xs text-status-info">· tiene secret</span>
        ) : null}
        {!client.pkce_required ? (
          <span
            className="text-xs rounded-full px-2 py-0.5 bg-status-warning/20 text-status-warning"
            title="oauth-token saltea la verificación PKCE para este client (login preset)"
          >
            PKCE off · preset
          </span>
        ) : null}
        {overrideCount > 0 ? (
          <span
            className="text-xs rounded-full px-2 py-0.5 bg-brand/15 text-brand"
            title="Este client overridea parámetros del partner"
          >
            {overrideCount} override{overrideCount === 1 ? '' : 's'}
          </span>
        ) : null}
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={() => setOverridesOpen(true)}
            data-help="partner.clients.overrides"
            className="text-sm text-brand hover:underline"
          >
            Overrides
          </button>
          <button
            type="button"
            onClick={() => setEditing(!editing)}
            disabled={!canWrite}
            className="text-sm text-ink-muted hover:text-ink dark:hover:text-ink-inverted hover:underline disabled:opacity-50"
          >
            {editing ? 'Cancelar' : 'Editar redirects'}
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={!canWrite}
            className="text-sm text-status-error hover:underline disabled:opacity-50"
          >
            Eliminar
          </button>
        </div>
      </div>
      <p className="text-xs text-ink-muted">
        grants: {client.grant_types.join(', ')} · scopes: {client.scopes_allowed.join(', ')}
      </p>
      {editing ? (
        <div className="space-y-2">
          <label className="text-xs text-ink-muted block">
            Redirect URIs (una por línea):
            <textarea
              rows={3}
              value={redirects}
              onChange={(e) => setRedirects(e.target.value)}
              className={fieldCls({ size: 'sm', mono: true, extra: 'mt-1' })}
            />
          </label>
          <div className="flex items-center justify-between gap-2 rounded border border-surface-border dark:border-surface-dark-border px-2 py-1.5">
            <span className="text-xs text-ink-muted">
              PKCE obligatorio
              <span className="block text-[0.65rem] opacity-70">
                Apagalo solo para apps que no pueden enviar code_challenge (login preset iOS).
              </span>
            </span>
            <button
              type="button"
              onClick={() => togglePkce.mutate()}
              disabled={!canWrite || togglePkce.isPending}
              className={`min-h-[2rem] rounded px-3 text-xs font-medium disabled:opacity-50 ${
                client.pkce_required
                  ? 'bg-status-success/20 text-status-success'
                  : 'bg-status-warning/20 text-status-warning'
              }`}
            >
              {client.pkce_required ? 'ON' : 'OFF'}
            </button>
          </div>
          {save.isError ? (
            <p className="text-sm text-status-error">{errorMessage(save.error)}</p>
          ) : null}
          {togglePkce.isError ? (
            <p className="text-sm text-status-error">{errorMessage(togglePkce.error)}</p>
          ) : null}
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="min-h-[2rem] rounded bg-brand hover:bg-brand-hover text-white text-xs font-medium px-3 disabled:opacity-50"
          >
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      ) : (
        <ul className="text-xs text-ink-muted font-mono space-y-0.5">
          {client.redirect_uris.map((u) => (
            <li key={u}>{u}</li>
          ))}
        </ul>
      )}

      <div data-help="partner.clients.presets">
        <ClientPresetsSection client={client} canWrite={canWrite} />
      </div>

      <ClientOverridesModal
        open={overridesOpen}
        onClose={() => setOverridesOpen(false)}
        client={client}
        partner={partner}
        canWrite={canWrite}
        canEditDanger={canEditDanger}
        onSaved={onUpdated}
      />
    </li>
  );
}

// ---------------------------------------------------------------------------
// Login presets (URL estatica de arranque para clients restringidos: app iOS
// que no puede enviar params/headers en la URL saliente de login).
// Ver docs/architecture/login-presets.md.
// ---------------------------------------------------------------------------

// El kickoff vive en el mismo host que /authorize (el host OIDC del entorno),
// bajo el prefijo de tenant /partners/{partnerId}/. Ver docs/architecture/login-presets.md.
const OIDC_BASE: string =
  (import.meta.env['VITE_OIDC_BASE'] as string | undefined)?.replace(/\/+$/, '') ??
  'https://<oidc-host>';

function kickoffUrl(partnerId: string, presetId: string): string {
  return `${OIDC_BASE}/partners/${partnerId}/l/${presetId}`;
}

function ClientPresetsSection({
  client,
  canWrite,
}: {
  client: AdminClient;
  canWrite: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);

  const presetsQ = useQuery({
    queryKey: ['client-presets', client.client_id],
    queryFn: () => listClientPresets(client.client_id),
    enabled: open,
  });

  const del = useMutation({
    mutationFn: (presetId: string) => deleteClientPreset(client.client_id, presetId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['client-presets', client.client_id] }),
  });

  return (
    <div className="pt-2 mt-2 border-t border-surface-border dark:border-surface-dark-border">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-xs text-ink-muted hover:text-ink dark:hover:text-ink-inverted hover:underline"
      >
        {open ? '▾' : '▸'} Presets de arranque (URL estática)
      </button>

      {open ? (
        <div className="mt-2 space-y-2">
          {client.pkce_required ? (
            <p className="text-[0.7rem] text-status-warning">
              Este client tiene PKCE obligatorio. Los presets <strong>login</strong> y{' '}
              <strong>signup</strong> requieren apagar <strong>PKCE</strong> (en «Editar
              redirects») — si no, el token exchange fallará. Los presets{' '}
              <strong>subscribe</strong> no lo necesitan.
            </p>
          ) : null}

          {presetsQ.data && presetsQ.data.length === 0 ? (
            <p className="text-xs text-ink-muted">Sin presets.</p>
          ) : null}

          {presetsQ.data?.map((p) => (
            <div
              key={p.preset_id}
              className="rounded border border-surface-border dark:border-surface-dark-border p-2 space-y-1"
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs">{p.preset_id}</span>
                <span className="text-[0.65rem] rounded-full px-1.5 bg-brand/20 text-brand font-medium">
                  {p.kind}
                </span>
                {p.platform ? (
                  <span className="text-[0.65rem] rounded-full px-1.5 bg-ink-muted/20 text-ink-muted">
                    {p.platform}
                  </span>
                ) : null}
                {!p.enabled ? (
                  <span className="text-[0.65rem] text-status-error">deshabilitado</span>
                ) : null}
                <button
                  type="button"
                  onClick={() => del.mutate(p.preset_id)}
                  disabled={!canWrite || del.isPending}
                  className="ml-auto text-[0.7rem] text-status-error hover:underline disabled:opacity-50"
                >
                  Eliminar
                </button>
              </div>
              <CopyableUrl url={kickoffUrl(client.partner_id, p.preset_id)} />
              <p className="text-[0.65rem] text-ink-muted font-mono">
                {p.kind === 'subscribe'
                  ? `return_url → ${p.redirect_uri}${p.plan_id ? ` · plan ${p.plan_id}` : ''}`
                  : `→ ${p.redirect_uri} · ${p.scope}`}
              </p>
            </div>
          ))}

          {adding ? (
            <PresetForm
              client={client}
              onClose={() => setAdding(false)}
              onSaved={() => {
                setAdding(false);
                qc.invalidateQueries({ queryKey: ['client-presets', client.client_id] });
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              disabled={!canWrite}
              className="text-xs text-brand hover:underline disabled:opacity-50"
            >
              + Nuevo preset
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}

function CopyableUrl({ url }: { url: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="w-full text-left font-mono text-[0.7rem] rounded bg-surface-muted dark:bg-surface-dark-muted px-2 py-1 hover:ring-1 hover:ring-brand break-all"
      title="Copiar URL de arranque"
    >
      {copied ? '✓ copiado' : url}
    </button>
  );
}

function PresetForm({
  client,
  onClose,
  onSaved,
}: {
  client: AdminClient;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [presetId, setPresetId] = useState('');
  const [kind, setKind] = useState<PresetKind>('login');
  const [redirectUri, setRedirectUri] = useState(client.redirect_uris[0] ?? '');
  const [scope, setScope] = useState(client.scopes_allowed.join(' '));
  const [platform, setPlatform] = useState('ios');
  const [planId, setPlanId] = useState('');

  const isOauth = kind === 'login' || kind === 'signup';
  // login/signup pasan por el code+PKCE exchange → el client debe tener PKCE off.
  const pkceBlocks = isOauth && client.pkce_required;

  // Planes del partner, solo para el dropdown de subscribe (pre-selección opcional).
  const plansQ = useQuery({
    queryKey: ['admin-plans', client.partner_id],
    queryFn: () => listPartnerPlans(client.partner_id),
    enabled: kind === 'subscribe',
  });

  const save = useMutation({
    mutationFn: () => {
      const body: LoginPresetUpsertBody = {
        kind,
        redirect_uri: redirectUri,
        scope: isOauth ? scope.trim() : 'openid profile',
        enabled: true,
      };
      const pl = platform.trim();
      if (pl) body.platform = pl;
      if (kind === 'subscribe' && planId) body.plan_id = planId;
      return upsertClientPreset(client.client_id, presetId.trim(), body);
    },
    onSuccess: onSaved,
  });

  return (
    <div className="rounded border border-brand/40 p-2 space-y-2">
      <label className="text-[0.7rem] text-ink-muted block">
        preset_id (slug: a-z 0-9 _ -)
        <input
          value={presetId}
          onChange={(e) => setPresetId(e.target.value)}
          placeholder={kind === 'subscribe' ? 'ios-plan' : 'ios-prod'}
          className={fieldCls({ size: 'sm', mono: true, extra: 'mt-0.5' })}
        />
      </label>
      <label className="text-[0.7rem] text-ink-muted block">
        flujo (kind)
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as PresetKind)}
          className={fieldCls({ size: 'sm', mono: true, extra: 'mt-0.5' })}
        >
          <option value="login">login → /authorize</option>
          <option value="signup">signup → /authorize (alta)</option>
          <option value="subscribe">subscribe → /subscribe (cambiar plan)</option>
        </select>
      </label>
      <label className="text-[0.7rem] text-ink-muted block">
        {kind === 'subscribe' ? 'return_url' : 'redirect_uri'} (de los registrados en el client)
        <select
          value={redirectUri}
          onChange={(e) => setRedirectUri(e.target.value)}
          className={fieldCls({ size: 'sm', mono: true, extra: 'mt-0.5' })}
        >
          {client.redirect_uris.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
      </label>
      {isOauth ? (
        <label className="text-[0.7rem] text-ink-muted block">
          scope (separado por espacios, ⊂ scopes del client)
          <input
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className={fieldCls({ size: 'sm', mono: true, extra: 'mt-0.5' })}
          />
        </label>
      ) : (
        <label className="text-[0.7rem] text-ink-muted block">
          plan a pre-seleccionar (opcional)
          <select
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            className={fieldCls({ size: 'sm', mono: true, extra: 'mt-0.5' })}
          >
            <option value="">— ninguno (el usuario elige) —</option>
            {plansQ.data?.map((p) => (
              <option key={p.plan_id} value={p.plan_id}>
                {p.display_name} ({p.code})
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="text-[0.7rem] text-ink-muted block">
        platform (etiqueta)
        <input
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          className={fieldCls({ size: 'sm', mono: true, extra: 'mt-0.5' })}
        />
      </label>
      {pkceBlocks ? (
        <p className="text-[0.7rem] text-status-warning">
          Los presets <strong>{kind}</strong> requieren PKCE apagado en este client.
          Apagalo en «Editar redirects» primero.
        </p>
      ) : null}
      {save.isError ? (
        <p className="text-[0.7rem] text-status-error">{errorMessage(save.error)}</p>
      ) : null}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending || presetId.trim().length < 2 || pkceBlocks}
          className="min-h-[2rem] rounded bg-brand hover:bg-brand-hover text-white text-xs font-medium px-3 disabled:opacity-50"
        >
          {save.isPending ? 'Guardando…' : 'Guardar preset'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="min-h-[2rem] rounded text-xs text-ink-muted hover:underline px-2"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

function CreateClientForm({
  partnerId,
  onClose,
  onCreated,
}: {
  partnerId: string;
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [form, setForm] = useState({
    client_id: 'cli_',
    client_type: 'public' as 'public' | 'confidential',
    client_secret: '',
    redirect_uris: '',
    grant_types: 'authorization_code,refresh_token',
    scopes_allowed: 'openid,profile',
    token_endpoint_auth_method: 'none' as AdminClient['token_endpoint_auth_method'],
  });

  const mut = useMutation({
    mutationFn: () =>
      createClientApi(partnerId, {
        client_id: form.client_id,
        client_type: form.client_type,
        ...(form.client_secret ? { client_secret: form.client_secret } : {}),
        redirect_uris: form.redirect_uris
          .split(/[\n,]/)
          .map((l) => l.trim())
          .filter(Boolean),
        grant_types: form.grant_types
          .split(',')
          .map((l) => l.trim())
          .filter(Boolean),
        scopes_allowed: form.scopes_allowed
          .split(',')
          .map((l) => l.trim())
          .filter(Boolean),
        token_endpoint_auth_method: form.token_endpoint_auth_method,
      }),
    onSuccess: () => {
      onCreated();
      onClose();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mut.mutate();
      }}
      className="bg-surface-muted dark:bg-surface-dark-muted rounded-xl border border-surface-border dark:border-surface-dark-border p-4 space-y-3"
    >
      <h3 className="text-sm font-medium" data-help="partner.clients.new">
        Nuevo OAuth client
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="client_id (debe empezar con cli_)">
          <input
            type="text"
            required
            value={form.client_id}
            onChange={(e) => setForm({ ...form, client_id: e.target.value })}
            className={fieldCls({ mono: true })}
          />
        </Field>
        <Field label="Type">
          <select
            value={form.client_type}
            onChange={(e) => {
              const t = e.target.value as 'public' | 'confidential';
              setForm({
                ...form,
                client_type: t,
                token_endpoint_auth_method: t === 'public' ? 'none' : 'client_secret_basic',
              });
            }}
            className={fieldCls()}
          >
            <option value="public">public (SPA, mobile)</option>
            <option value="confidential">confidential (server-side)</option>
          </select>
        </Field>
        {form.client_type === 'confidential' ? (
          <Field label="Client secret (se guarda solo el hash)">
            <input
              type="password"
              required
              minLength={16}
              value={form.client_secret}
              onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
              className={fieldCls({ mono: true })}
            />
          </Field>
        ) : null}
        <Field label="Redirect URIs (una por línea)">
          <textarea
            rows={2}
            required
            value={form.redirect_uris}
            onChange={(e) => setForm({ ...form, redirect_uris: e.target.value })}
            className={fieldCls({ size: 'sm', mono: true })}
          />
        </Field>
        <Field label="Grant types (coma separadas)">
          <input
            type="text"
            value={form.grant_types}
            onChange={(e) => setForm({ ...form, grant_types: e.target.value })}
            className={fieldCls({ mono: true })}
          />
        </Field>
        <Field label="Scopes (coma separadas)">
          <input
            type="text"
            value={form.scopes_allowed}
            onChange={(e) => setForm({ ...form, scopes_allowed: e.target.value })}
            className={fieldCls({ mono: true })}
          />
        </Field>
      </div>
      {mut.isError ? (
        <p className="text-sm text-status-error">{errorMessage(mut.error)}</p>
      ) : null}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="min-h-[2.5rem] rounded border border-surface-border dark:border-surface-dark-border px-3 text-sm"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={mut.isPending}
          className="min-h-[2.5rem] rounded bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 disabled:opacity-50"
        >
          {mut.isPending ? 'Creando…' : 'Crear client'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Terms tab — versionado de T&C (ADR 0008)
// ---------------------------------------------------------------------------

function TermsTab({ partnerId, canWrite }: { partnerId: string; canWrite: boolean }): JSX.Element {
  const qc = useQueryClient();
  const tosQ = useQuery({
    queryKey: ['admin-partner-tos', partnerId],
    queryFn: () => listPartnerTosVersions(partnerId),
  });

  const [version, setVersion] = useState('');
  const [tosUrl, setTosUrl] = useState('');
  const [graceDays, setGraceDays] = useState(7);
  const [effectiveAt, setEffectiveAt] = useState('');

  const publish = useMutation({
    mutationFn: () =>
      publishTosVersion(partnerId, {
        version: version.trim(),
        tos_url: tosUrl.trim(),
        grace_days: graceDays,
        ...(effectiveAt ? { effective_at: new Date(effectiveAt).toISOString() } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-partner-tos', partnerId] });
      setVersion('');
      setTosUrl('');
      setGraceDays(7);
      setEffectiveAt('');
    },
  });

  const current = tosQ.data?.current_version ?? null;
  const versions = tosQ.data?.versions ?? [];

  return (
    <div className="space-y-6">
      <p className="text-xs text-ink-muted max-w-2xl">
        Al publicar una versión nueva, los usuarios existentes reciben un aviso y tienen{' '}
        <strong>grace_days</strong> para aceptarla; los nuevos la aceptan en el signup. La versión
        vigente es la de mayor fecha de vigencia ya alcanzada.
      </p>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold">Versiones publicadas</h3>
        {tosQ.isLoading ? (
          <p className="text-sm text-ink-muted">Cargando…</p>
        ) : versions.length === 0 ? (
          <p className="text-sm text-ink-muted">Todavía no hay versiones publicadas.</p>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-ink-muted border-b border-surface-border dark:border-surface-dark-border">
                <th className="py-2 pr-3">Versión</th>
                <th className="py-2 pr-3">Vigencia</th>
                <th className="py-2 pr-3">Gracia</th>
                <th className="py-2 pr-3">URL</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr
                  key={v.version}
                  className="border-b border-surface-border dark:border-surface-dark-border"
                >
                  <td className="py-2 pr-3 font-medium">
                    {v.version}
                    {v.version === current ? (
                      <span className="ml-2 rounded bg-status-success/15 text-status-success text-xs px-1.5 py-0.5">
                        vigente
                      </span>
                    ) : null}
                  </td>
                  <td className="py-2 pr-3">{new Date(v.effective_at).toLocaleDateString('es-AR')}</td>
                  <td className="py-2 pr-3">{v.grace_days}d</td>
                  <td className="py-2 pr-3 truncate max-w-[16rem]">
                    <a href={v.tos_url} target="_blank" rel="noopener noreferrer" className="underline">
                      {v.tos_url}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          publish.mutate();
        }}
        className="space-y-4 pt-4 border-t border-surface-border dark:border-surface-dark-border"
      >
        <h3 className="text-sm font-semibold">Publicar nueva versión</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Versión">
            <input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              required
              placeholder="2.0"
              className={fieldCls({ size: 'lg' })}
            />
          </Field>
          <Field label="Días de gracia">
            <input
              type="number"
              min={0}
              max={365}
              value={graceDays}
              onChange={(e) => setGraceDays(Number(e.target.value))}
              required
              className={fieldCls({ size: 'lg' })}
            />
          </Field>
        </div>
        <Field label="URL del documento de T&C">
          <input
            type="url"
            value={tosUrl}
            onChange={(e) => setTosUrl(e.target.value)}
            required
            placeholder="https://…"
            className={fieldCls({ size: 'lg' })}
          />
        </Field>
        <Field label="Vigente desde (opcional — default ahora)">
          <input
            type="datetime-local"
            value={effectiveAt}
            onChange={(e) => setEffectiveAt(e.target.value)}
            className={fieldCls({ size: 'lg' })}
          />
        </Field>

        {publish.isError ? (
          <p className="text-xs text-status-error">{errorMessage(publish.error)}</p>
        ) : null}
        {publish.isSuccess ? (
          <p className="text-xs text-status-success">Versión publicada.</p>
        ) : null}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!canWrite || publish.isPending}
            className="min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 disabled:opacity-50"
          >
            {publish.isPending ? 'Publicando…' : 'Publicar versión'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Danger Zone tab — switches sensibles + configuracion de medios de pago.
// Solo se monta con el permiso danger_zone (el backend re-verifica igual).
// ---------------------------------------------------------------------------

function DangerZoneTab({ partner }: { partner: PartnerDetail }): JSX.Element {
  return (
    <div className="space-y-8 max-w-3xl">
      <div
        role="note"
        className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error"
      >
        Zona sensible: estos cambios afectan el login, el alta y los cobros de{' '}
        <strong>{partner.display_name}</strong> en el momento. Cada acción queda registrada en el
        audit log.
      </div>

      <DangerSwitches partner={partner} />
      <div data-help="partner.danger.payments">
        <PaymentProvidersSection partner={partner} />
      </div>
      <div data-help="partner.danger.invoicing">
        <InvoicingSection partner={partner} />
      </div>
    </div>
  );
}

// ---- Facturación electrónica (ARCA / Facturante) ---------------------------
//
// Cierra el recorrido de la regla de backoffice auto-gestionado: cargar las
// credenciales, PROBARLAS y encender el circuito sin salir de la pantalla.
// Antes esto exigía AWS CLI para el secreto y una llamada a la API para el
// interruptor.
//
// El interruptor se deshabilita mientras la configuración no esté completa y
// probada. El backend rechaza igual (es la guarda de verdad), pero un botón que
// se puede apretar para recibir un error es una invitación a romper algo.

function InvoicingSection({ partner }: { partner: PartnerDetail }): JSX.Element {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});
  // El IVA se edita aparte de `draft`: no es un campo de Facturante y no viaja
  // en `values`. `null` = sin tocar (se muestra lo guardado).
  const [vatDraft, setVatDraft] = useState<string | null>(null);
  const [probe, setProbe] = useState<InvoicingProbe | null>(null);
  const [saved, setSaved] = useState(false);

  const configQ = useQuery({
    queryKey: ['admin-partner-invoicing-config', partner.partner_id],
    queryFn: () => fetchPartnerInvoicingConfig(partner.partner_id),
  });

  // Si activar falla porque Facturante no aceptó las credenciales, se ofrece
  // forzar — pero solo después de haberlo intentado bien. Un botón de "forzar"
  // siempre visible se convierte en el botón que todos aprietan.
  const [enableFailed, setEnableFailed] = useState(false);

  const save = useMutation({
    mutationFn: (body: {
      enabled?: boolean;
      values?: Record<string, string>;
      probe?: boolean;
      force?: boolean;
      vat_rate?: number;
    }) => putPartnerInvoicingConfig(partner.partner_id, body),
    onSuccess: (res) => {
      setDraft({});
      setVatDraft(null);
      setProbe(res.probe);
      setSaved(true);
      setEnableFailed(false);
      qc.setQueryData(['admin-partner-invoicing-config', partner.partner_id], res);
      qc.invalidateQueries({ queryKey: ['admin-partner-detail', partner.partner_id] });
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (_e, vars) => {
      if (vars.enabled === true) setEnableFailed(true);
    },
  });

  const testConnection = useMutation({
    mutationFn: () => fetchPartnerInvoicingConfig(partner.partner_id, true),
    onSuccess: (res) => setProbe(res.probe),
  });

  if (configQ.isLoading) {
    return <p className="text-sm text-ink-muted">Cargando facturación electrónica…</p>;
  }
  if (configQ.isError || !configQ.data) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error"
      >
        No pudimos cargar la configuración de facturación. {errorMessage(configQ.error)}
      </div>
    );
  }

  const cfg = configQ.data;
  const vatValue = vatDraft ?? (cfg.vat_rate === null ? '' : String(cfg.vat_rate));
  const vatParsed = vatValue.trim() === '' ? null : Number(vatValue);
  const vatDirty =
    vatDraft !== null && vatParsed !== null && Number.isFinite(vatParsed) && vatParsed !== cfg.vat_rate;
  const dirty = Object.keys(draft).length > 0 || vatDirty;
  // Sin alícuota cargada el emisor rechaza cada cobro (frena antes de emitir una
  // factura con IVA 0%), así que es tan bloqueante como una credencial faltante.
  const vatMissing = !cfg.vat_rate || cfg.vat_rate <= 0;
  const canEnable = cfg.missing_required.length === 0 && cfg.valid && !vatMissing;

  function fieldValue(f: InvoicingFieldDef): string {
    if (f.key in draft) return draft[f.key] ?? '';
    if (f.secret) return ''; // los secretos jamás vuelven del backend
    const stored = cfg.values[f.key];
    return stored === undefined ? '' : String(stored);
  }

  return (
    <section className="space-y-3 border-t border-surface-border dark:border-surface-dark-border pt-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold">Facturación electrónica (ARCA)</h2>
        {cfg.enabled ? (
          <span className="text-xs rounded-full px-2 py-0.5 font-medium bg-status-success/15 text-status-success">
            activa
          </span>
        ) : (
          <span className="text-xs rounded-full px-2 py-0.5 font-medium bg-ink-muted/15 text-ink-muted">
            apagada
          </span>
        )}
      </div>
      <p className="text-xs text-ink-muted max-w-3xl">
        Con esto activo, cada cobro acreditado emite una Factura B a consumidor final vía
        Facturante. Los campos marcados como secretos son de solo carga: se pueden reemplazar pero
        nunca volver a leer. Dejá la contraseña vacía para conservar la actual.
      </p>

      {cfg.configured && !cfg.valid ? (
        <div
          role="alert"
          className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-xs text-status-error"
        >
          La configuración guardada no se puede interpretar: {cfg.validation_error}
        </div>
      ) : null}
      {cfg.missing_required.length > 0 ? (
        <div
          role="status"
          className="rounded-lg border border-status-warning/30 bg-status-warning/10 p-3 text-xs text-status-warning"
        >
          Faltan datos requeridos: {cfg.missing_required.join(', ')}. Hasta completarlos no se
          puede activar la facturación.
        </div>
      ) : null}
      {vatMissing ? (
        <div
          role="status"
          className="rounded-lg border border-status-warning/30 bg-status-warning/10 p-3 text-xs text-status-warning"
        >
          El partner no tiene alícuota de IVA cargada. Con IVA en 0 el emisor rechaza cada cobro en
          vez de emitir un comprobante mal formado, así que hasta cargarla no se puede activar la
          facturación.
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!dirty) return;
          save.mutate({
            ...(Object.keys(draft).length > 0 ? { values: draft } : {}),
            ...(vatDirty && vatParsed !== null ? { vat_rate: vatParsed } : {}),
          });
        }}
        aria-busy={save.isPending ? 'true' : undefined}
        className="space-y-3 max-w-3xl"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Va primero y fuera de cfg.fields a propósito: no es un parámetro de
              Facturante sino del partner, y es el que bloquea el encendido. */}
          <label className="text-sm space-y-1">
            <span className="font-medium">
              Alícuota de IVA (%)<span className="text-status-error"> *</span>
            </span>
            <input
              type="number"
              min={0}
              max={100}
              step={0.01}
              inputMode="decimal"
              autoComplete="off"
              value={vatValue}
              placeholder="21"
              onChange={(e) => setVatDraft(e.target.value)}
              className={fieldCls()}
            />
            <span className="block text-[11px] text-ink-muted">
              21 para AFA/LPF Play. Decide el desglose neto/IVA de la factura y del asiento
              contable, así que también se aplica con la facturación apagada. Cambiarlo no toca
              los cobros ya registrados.
            </span>
          </label>
          {cfg.fields.map((f) => (
            <label key={f.key} className="text-sm space-y-1">
              <span className="font-medium">
                {f.label}
                {f.required ? <span className="text-status-error"> *</span> : null}
                {f.secret && cfg.secrets_set[f.key] ? (
                  <span className="ml-2 text-[11px] font-normal text-status-success">
                    ● cargado
                  </span>
                ) : null}
              </span>
              <input
                type={f.secret ? 'password' : f.type === 'number' ? 'number' : 'text'}
                autoComplete={f.secret ? 'new-password' : 'off'}
                value={fieldValue(f)}
                placeholder={
                  f.secret && cfg.secrets_set[f.key] ? '•••••••• (sin cambios)' : f.placeholder
                }
                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                className={fieldCls()}
              />
              {f.help ? <span className="block text-[11px] text-ink-muted">{f.help}</span> : null}
            </label>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="submit"
            disabled={!dirty || save.isPending}
            className="min-h-[2.5rem] rounded-lg bg-brand hover:bg-brand-hover text-white font-medium px-5 focus-visible:ring-2 focus-visible:ring-brand-light disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </button>
          <button
            type="button"
            disabled={!cfg.configured || testConnection.isPending}
            onClick={() => {
              setProbe(null);
              testConnection.mutate();
            }}
            title="Autentica contra Facturante con las credenciales guardadas. No emite ningún comprobante."
            className="min-h-[2.5rem] rounded-lg bg-status-info/15 text-status-info hover:bg-status-info/25 px-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testConnection.isPending ? 'Probando…' : 'Probar conexión'}
          </button>
          {saved ? <span className="text-xs text-status-success">Guardado.</span> : null}
          {save.isError ? (
            <span className="text-xs text-status-error">{errorMessage(save.error)}</span>
          ) : null}
        </div>
      </form>

      {probe ? (
        <div
          role="status"
          className={`rounded-lg border p-3 text-xs max-w-3xl ${
            probe.ok
              ? 'border-status-success/30 bg-status-success/10 text-status-success'
              : 'border-status-error/30 bg-status-error/10 text-status-error'
          }`}
        >
          {probe.message}
          {probe.latency_ms !== undefined ? ` (${probe.latency_ms} ms)` : ''}
        </div>
      ) : null}

      <div className="space-y-2 pt-1">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={save.isPending || (!cfg.enabled && !canEnable)}
            onClick={() => save.mutate({ enabled: !cfg.enabled })}
            className={`min-h-[2.5rem] rounded-lg px-4 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
              cfg.enabled
                ? 'bg-status-error/15 text-status-error hover:bg-status-error/25'
                : 'bg-status-success/15 text-status-success hover:bg-status-success/25'
            }`}
          >
            {cfg.enabled ? 'Apagar facturación' : 'Activar facturación'}
          </button>
          <span className="text-xs text-ink-muted max-w-md">
            {cfg.enabled
              ? 'Se puede apagar en cualquier momento: los cobros siguen, solo dejan de facturarse.'
              : canEnable
                ? 'Al activar se prueban las credenciales contra Facturante: si no las acepta, no se activa.'
                : 'Completá los datos requeridos para poder activar.'}
          </span>
        </div>

        {save.isError && enableFailed ? (
          <div
            role="alert"
            className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-xs text-status-error max-w-3xl space-y-2"
          >
            <p>{errorMessage(save.error)}</p>
            <button
              type="button"
              onClick={() => save.mutate({ enabled: true, force: true })}
              className="rounded-lg border border-status-error/40 px-3 py-1 font-medium hover:bg-status-error/10"
            >
              Activar igual
            </button>
            <span className="ml-2 text-ink-muted">
              Usalo solo si sabés que el problema es de Facturante y no de las credenciales: con
              credenciales malas, cada cobro va a quedar sin comprobante.
            </span>
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ---- Switches -------------------------------------------------------------

function DangerSwitches({ partner }: { partner: PartnerDetail }): JSX.Element {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState<null | {
    title: string;
    description: string;
    apply: () => Promise<void>;
  }>(null);
  const [error, setError] = useState<string | null>(null);

  const plansEnabled = partner.features['plans_enabled'] === true;

  const mutate = useMutation({
    mutationFn: async (apply: () => Promise<void>) => apply(),
    onSuccess: () => {
      setConfirming(null);
      setError(null);
      qc.invalidateQueries({ queryKey: ['admin-partner-detail', partner.partner_id] });
    },
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <section className="space-y-3" data-help="partner.danger.switches">
      <h2 className="text-lg font-semibold">Switches</h2>

      <DangerSwitchRow
        label="Verificación de email obligatoria en el login"
        description="Encendido: un usuario con email sin verificar no puede iniciar sesión. Apagado: el login acepta cuentas sin verificar (ej. incidente de quota de mails)."
        checked={partner.require_email_verification}
        onToggle={(next) =>
          setConfirming({
            title: next ? 'Exigir email verificado' : 'Dejar de exigir email verificado',
            description: next
              ? 'Los usuarios con email sin verificar van a dejar de poder loguearse. ¿Continuar?'
              : 'Usuarios con email sin verificar van a poder loguearse. Usalo solo como medida transitoria. ¿Continuar?',
            apply: () =>
              patchPartner(partner.partner_id, { require_email_verification: next }),
          })
        }
      />

      <DangerSwitchRow
        label="Excepción del Inspector a la verificación de email"
        description="Solo aplica al login del Inspector Portal (entorno de pruebas). Encendido: los integradores pueden inspeccionar usuarios de prueba sin verificar. No afecta al login normal, que sigue el switch de arriba. Inerte si la verificación no es obligatoria, e inerte en producción."
        checked={partner.inspector_skip_email_verification}
        onToggle={(next) =>
          setConfirming({
            title: next ? 'Eximir al Inspector' : 'Quitar la excepción del Inspector',
            description: next
              ? 'El login del Inspector va a aceptar cuentas sin verificar. El login normal no cambia. ¿Continuar?'
              : 'El Inspector va a exigir email verificado igual que el login normal. ¿Continuar?',
            apply: () =>
              patchPartner(partner.partner_id, { inspector_skip_email_verification: next }),
          })
        }
      />

      <DangerSwitchRow
        label="Paso de selección de plan en el registro"
        description="Encendido: el wizard de signup muestra la selección de plan y habilita el circuito comercial (checkout, cambios de plan). Apagado: el alta saltea el paso de plan."
        checked={plansEnabled}
        onToggle={(next) =>
          setConfirming({
            title: next ? 'Activar venta de planes' : 'Desactivar venta de planes',
            description: next
              ? 'El signup va a pedir plan y el checkout queda habilitado. Verificá que los planes y el medio de pago estén configurados. ¿Continuar?'
              : 'El signup saltea el plan y el checkout devuelve feature_disabled (las confirmaciones de pagos ya iniciados siguen funcionando). ¿Continuar?',
            apply: () =>
              patchPartner(partner.partner_id, {
                features: { ...partner.features, plans_enabled: next },
              }),
          })
        }
      />

      {error ? (
        <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
          {error}
        </div>
      ) : null}

      <ConfirmDialog
        open={confirming !== null}
        title={confirming?.title ?? ''}
        description={confirming?.description ?? ''}
        danger
        loading={mutate.isPending}
        onConfirm={() => confirming && mutate.mutate(confirming.apply)}
        onCancel={() => setConfirming(null)}
      />
    </section>
  );
}

function DangerSwitchRow({
  label,
  description,
  checked,
  onToggle,
}: {
  label: string;
  description: string;
  checked: boolean;
  onToggle: (next: boolean) => void;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-surface-border dark:border-surface-dark-border p-4">
      <div className="space-y-1">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-ink-muted">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onToggle(!checked)}
        className={`relative shrink-0 h-7 w-12 rounded-full transition focus-visible:ring-2 focus-visible:ring-brand-light ${
          checked ? 'bg-status-success' : 'bg-surface-border dark:bg-surface-dark-border'
        }`}
      >
        <span
          className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${
            checked ? 'left-6' : 'left-1'
          }`}
        />
      </button>
    </div>
  );
}

// ---- Medios de pago ---------------------------------------------------------

function PaymentProvidersSection({ partner }: { partner: PartnerDetail }): JSX.Element {
  const configQ = useQuery({
    queryKey: ['admin-partner-payment-config', partner.partner_id],
    queryFn: () => fetchPartnerPaymentConfig(partner.partner_id),
  });
  const [activeProvider, setActiveProvider] = useState<string | null>(null);

  if (configQ.isLoading) {
    return <p className="text-sm text-ink-muted">Cargando medios de pago…</p>;
  }
  if (configQ.isError || !configQ.data) {
    return (
      <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
        No pudimos cargar la configuración de medios de pago. {errorMessage(configQ.error)}
      </div>
    );
  }

  const providers = configQ.data.providers;
  const active =
    providers.find((p) => p.provider === activeProvider) ??
    providers.find((p) => p.is_default) ??
    providers[0];

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">Medios de pago</h2>
      <p className="text-xs text-ink-muted">
        Los parámetros se guardan por partner. Los campos marcados como secretos son de solo
        carga: se pueden reemplazar pero nunca volver a leer.
      </p>

      <nav aria-label="Medios de pago" className="flex gap-1 border-b border-surface-border dark:border-surface-dark-border">
        {providers.map((p) => (
          <button
            key={p.provider}
            type="button"
            onClick={() => setActiveProvider(p.provider)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${
              active?.provider === p.provider
                ? 'border-brand text-brand font-medium'
                : 'border-transparent text-ink-muted hover:text-ink dark:hover:text-ink-inverted'
            }`}
          >
            {p.display_name}
            {p.is_default ? <Check size={12} className="inline -mt-0.5 ml-1" aria-label="default" /> : null}
            {!p.implemented ? ' (próximamente)' : ''}
          </button>
        ))}
      </nav>

      {active ? (
        <ProviderConfigForm key={active.provider} partnerId={partner.partner_id} entry={active} />
      ) : null}
    </section>
  );
}

function ProviderConfigForm({
  partnerId,
  entry,
}: {
  partnerId: string;
  entry: PaymentProviderConfigEntry;
}): JSX.Element {
  const qc = useQueryClient();
  // Draft: solo los campos tocados. Los secretos arrancan siempre vacios.
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      // Mandar SOLO lo tocado. '' en un campo plano = borrarlo; en un secreto
      // el backend lo ignora (para rotar se pisa con el valor nuevo).
      const values: Record<string, string | null> = {};
      for (const [k, v] of Object.entries(draft)) values[k] = v;
      return putPartnerPaymentConfig(partnerId, entry.provider, values, setAsDefault);
    },
    onSuccess: () => {
      setDraft({});
      setSetAsDefault(false);
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['admin-partner-payment-config', partnerId] });
      qc.invalidateQueries({ queryKey: ['admin-partner-detail', partnerId] });
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const dirty = Object.keys(draft).length > 0 || setAsDefault;

  function fieldValue(key: string): string {
    if (key in draft) return draft[key] ?? '';
    return entry.values[key] ?? '';
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (dirty) save.mutate();
      }}
      aria-busy={save.isPending ? 'true' : undefined}
      className="space-y-4"
    >
      <p className="text-xs text-ink-muted">{entry.description}</p>

      {entry.missing_required.length > 0 ? (
        <div role="status" className="rounded-lg border border-status-warning/30 bg-status-warning/10 p-3 text-xs text-status-warning">
          Faltan campos requeridos: {entry.missing_required.join(', ')}. El medio de pago no queda
          operativo hasta completarlos.
        </div>
      ) : null}
      {entry.env_fallback_active.length > 0 ? (
        <div role="status" className="rounded-lg border border-status-warning/30 bg-status-warning/10 p-3 text-xs text-status-warning">
          Usando configuración global de transición (env) para: {entry.env_fallback_active.join(', ')}.
          Cargalos acá para que este partner deje de depender de esa configuración.
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {entry.fields.map((f) =>
          f.type === 'rows' ? (
            <div key={f.key} className="md:col-span-2">
              <RowsField
                field={f}
                value={fieldValue(f.key)}
                onChange={(json) => setDraft({ ...draft, [f.key]: json })}
              />
            </div>
          ) : (
          <Field key={f.key} label={`${f.label}${f.required ? ' *' : ''}`}>
            {f.type === 'select' ? (
              <select
                value={fieldValue(f.key)}
                onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                className={fieldCls({ size: 'lg' })}
              >
                <option value="">(sin valor)</option>
                {(f.options ?? []).map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            ) : (
              <input
                type={f.secret ? 'password' : f.type === 'url' ? 'url' : 'text'}
                value={f.secret ? draft[f.key] ?? '' : fieldValue(f.key)}
                onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                placeholder={
                  f.secret
                    ? entry.secrets[f.key]?.is_set
                      ? '•••••• configurado — escribí para reemplazar'
                      : f.placeholder ?? ''
                    : f.placeholder ?? ''
                }
                autoComplete="off"
                className={fieldCls({ size: 'lg' })}
              />
            )}
            {f.help ? <span className="block text-xs text-ink-muted font-normal">{f.help}</span> : null}
          </Field>
          ),
        )}
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={setAsDefault}
          onChange={(e) => setSetAsDefault(e.target.checked)}
          disabled={entry.is_default || !entry.implemented}
        />
        {entry.is_default
          ? 'Es el medio de pago activo de este partner'
          : `Usar ${entry.display_name} como medio de pago activo`}
      </label>

      {save.isError ? (
        <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
          {errorMessage(save.error)}
        </div>
      ) : null}
      {saved ? (
        <div role="status" className="rounded-lg border border-status-success/30 bg-status-success/10 p-3 text-sm text-status-success">
          Guardado.
        </div>
      ) : null}

      <button
        type="submit"
        disabled={!dirty || save.isPending}
        className="min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white font-medium px-6 py-2 focus-visible:ring-2 focus-visible:ring-brand-light disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {save.isPending ? 'Guardando…' : `Guardar ${entry.display_name}`}
      </button>
    </form>
  );
}

/**
 * Campo `rows`: tabla editable (marca → número de establecimiento, etc.). El
 * valor viaja al draft como JSON string. Muestra un preview inline + un modal
 * con la tabla (agregar/quitar filas). Los cambios se reflejan en vivo en el
 * draft, así que "Guardar {provider}" persiste la tabla como cualquier campo.
 */
function RowsField({
  field,
  value,
  onChange,
}: {
  field: PaymentProviderFieldDef;
  value: string;
  onChange: (json: string) => void;
}): JSX.Element {
  const columns = field.columns ?? [];
  const [open, setOpen] = useState(false);

  const rows = useMemo<Array<Record<string, string>>>(() => {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as Array<Record<string, string>>) : [];
    } catch {
      return [];
    }
  }, [value]);

  const commit = (next: Array<Record<string, string>>): void => onChange(JSON.stringify(next));
  const addRow = (): void => {
    const blank: Record<string, string> = {};
    for (const c of columns) blank[c.key] = '';
    commit([...rows, blank]);
  };
  const updateCell = (i: number, key: string, v: string): void =>
    commit(rows.map((r, idx) => (idx === i ? { ...r, [key]: v } : r)));
  const removeRow = (i: number): void => commit(rows.filter((_, idx) => idx !== i));

  return (
    <div className="text-sm space-y-1">
      <div className="font-medium">
        {field.label}
        {field.required ? ' *' : ''}
      </div>
      {field.help ? <p className="text-xs text-ink-muted font-normal">{field.help}</p> : null}

      {rows.length > 0 ? (
        <ul className="text-xs text-ink-muted space-y-0.5">
          {rows.map((r, i) => (
            <li key={i}>
              {columns
                .map((c) => r[c.key])
                .filter(Boolean)
                .join(' → ') || '(fila incompleta)'}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-ink-muted">Sin filas cargadas.</p>
      )}

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1 inline-flex items-center gap-1 rounded border border-surface-border dark:border-surface-dark-border px-3 py-1.5 text-xs hover:bg-surface-muted dark:hover:bg-surface-dark-muted"
      >
        Editar tabla ({rows.length})
      </button>

      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={field.label}
          className="fixed inset-0 z-50 bg-ink/50 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-surface dark:bg-surface-dark border border-surface-border dark:border-surface-dark-border rounded-xl shadow-lg w-full max-w-lg mt-12 p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-start justify-between gap-3">
              <h2 className="text-lg font-semibold">{field.label}</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Cerrar"
                className="text-ink-muted hover:text-ink dark:hover:text-ink-inverted"
              >
                <X size={18} />
              </button>
            </header>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-ink-muted">
                    {columns.map((c) => (
                      <th key={c.key} className="pb-1 pr-2 font-medium">
                        {c.label}
                      </th>
                    ))}
                    <th className="pb-1 w-8" aria-label="acciones" />
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length + 1} className="py-2 text-xs text-ink-muted">
                        Sin filas. Agregá una con el botón de abajo.
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, i) => (
                      <tr key={i}>
                        {columns.map((c) => (
                          <td key={c.key} className="py-1 pr-2 align-top">
                            {c.type === 'select' ? (
                              <select
                                value={r[c.key] ?? ''}
                                onChange={(e) => updateCell(i, c.key, e.target.value)}
                                className={fieldCls({ size: 'lg' })}
                              >
                                <option value="">(elegir)</option>
                                {(c.options ?? []).map((o) => (
                                  <option key={o} value={o}>
                                    {o}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                value={r[c.key] ?? ''}
                                onChange={(e) => updateCell(i, c.key, e.target.value)}
                                placeholder={c.placeholder ?? ''}
                                autoComplete="off"
                                className={fieldCls({ size: 'lg' })}
                              />
                            )}
                          </td>
                        ))}
                        <td className="py-1 align-top">
                          <button
                            type="button"
                            onClick={() => removeRow(i)}
                            aria-label="Eliminar fila"
                            className="text-status-error hover:opacity-70 p-1"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={addRow}
                className="inline-flex items-center gap-1 rounded border border-surface-border dark:border-surface-dark-border px-3 py-1.5 text-xs hover:bg-surface-muted dark:hover:bg-surface-dark-muted"
              >
                <Plus size={14} /> Agregar fila
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg bg-brand hover:bg-brand-hover text-white text-sm px-4 py-1.5"
              >
                Listo
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
